// The authoritative game: one per room, run by the server at 60 Hz.
// Owns robots, bullets, damage, priming, lives, waves and the pause rule.

import { NO_CONTRACT } from './progression';
import { DT, HISTORY_TICKS, LAG_COMP_MAX_TICKS, Rng, TICK_RATE, aimDir, clamp, hash32, len2, rand01, raySphere, secs } from './core';
import { AP, BARREL_DMG, PART_POP_FRAC, Part, HIT_SHOVE, HIT_STUN_MAX, HIT_STUN_PER_FRAC, ROBOT_HIT_STUN_MAX, BARREL_FUSE_TICKS, BARREL_HP, BARREL_R, BARREL_RADIUS, MUT_BLOOD, SEVER_FORWARD, SEVER_R, SHED_BLAST_DMG, SHED_CD_SECS, SHED_MAX, MUT_BRITTLE, MUT_SCRAP_DMG, MUT_SCRAP_R, Mut, AEGIS_DELAY_SECS, Affix, REDSHELL_MUL, ROUT_SECS, STALK_REVEAL, CHARGE_DET_BASE, CHARGE_DET_FULL, CHARGE_DMG_BASE, CHARGE_DMG_FULL, CLASSES, ClassId, shieldDelayFor,
  FLINCH_TICKS, GETUP, HIT_STUN, KNOCKDOWN, KNOCK_CD, KNOCK_DOWN, KNOCK_FLIGHT, KNOCK_MAX, KNOCK_RAGDOLL, KnockKind, STAGGER_CHARGE, STAGGER_DMG_MUL, STAGGER_MELEE, STAGGER_SECS,
  BLINK_CD, BLINK_CONE, BLINK_DMG, BLINK_HEAT, BLINK_HOVER_MUL, BLINK_IFRAMES, BLINK_KNOCK, BLINK_KNOCK_R, BLINK_RANGE, BLINK_STUN, HAMMER_CD, HAMMER_CONE, HAMMER_DMG, HAMMER_HEAT, HAMMER_RANGE,
  PHANTOM_JAB, POUND_DMG, POUND_H, POUND_HEAT, POUND_R0, POUND_R1, WARP_CLIP_FORCE, WARP_CLIP_R, WARP_GRACE,
  AP_CHIP_MUL, AP_EQUAL_MUL, RICOCHET_FRAC, RICOCHET_JITTER, RICOCHET_RANGE, RICOCHET_UP, WEAK_POINT_DOT, DETONATE_CLOAK, ENEMIES, SKY_PRIME_MUL, ENGINEER, EType, HULL_MAX, ICE_SLOW_MAX_STACKS, ICE_STACK_DECAY_SECS, MAGNETIC_SHIELD_BLOCK_SECS, POSSE_LIVES, PLAYER_RADIUS, PLAYER_HEIGHT, EYE_HEIGHT, isBossType } from './defs';
import { Ev, GameEvent } from './codec';
import { BKind, BULLETS, Bullet, HitSrc, advanceBullet, bulletHitsCapsule, makeBullet } from './bullets';
import { AiHost, EState, Enemy, historyAt, makeEnemy, recordHistory, startEngineerFlee, stepEnemy } from './enemies';
import { PerchBook, updatePerches } from './perch';
import { Btn, InputFrame, Move, Phase, PlayerState, Solid, StepOut, isTargetable, newPlayer, newStepOut, respawnAt, stepPlayer, modsOf } from './player';
import { CARDS, DraftKind, Mods, SET_BONUS, hullMaxOf, newMods, offerDraft, pickCard as pickFromOffer, rollDraft, setTier } from './cards';
import { ACID_VULN_MUL, CONFUSION_MUL, EL_DMG_MOD, ELEMS, Hazard, applyProc, clearElems, frozenMul, gunElem, gunLevel, hazardKind, isConfused, placeHazard, procPlayer, rollArmorEl, stepHazards, tryCombo, tryProc, weaknessMul } from './elements';
import { BONK_DMG_PER_MS, BONK_MAX, BONK_MIN, activeWeaponId, gunLevelDmg } from './player';
import { NO_GROUND, RayHit, World } from './world';
import { BossEv } from './boss';
import { BossBase, BossBeat, BossHit, BossKind, newBossHit } from './bosses/types';
import { bossDef } from './bosses/index';
import { Critter, CritterHost, critterNoise, critterRaycast, hurtCritter, stepCritters, CRITTERS, CState } from './critters';
import { goldKilled, BELL_R, clearSpawns, RunPhase, RunResult, RunState, bellPos, bossDefeated, isAboard, newRun, ringBell, runTier, startRun, stepRun, wipeRun } from './run';
import { nadeCap, Arsenal, HAZARD_POSSE_MUL } from './arsenal';
import { ELEM_PROC_CHANCE, MAGNET_CATCH, MAGNET_R, METAL_TAKE, PU, PU_COUNT, PowerUps, dropBuffs, immuneTo, puElem } from './powerups';
import { Elem, GRENADE_MAX, Loadout, OVERLOAD_DMG, OVERLOAD_STUN, Perk, Proj, STAGGER_RANGE, STAGGER_STUN, ShotMod, WeaponDef, staggerPellets, validLoadout, weaponDef, W, BULLET_GRAVITY, DROP_SEG, DROP_FREE } from './weapons';
import { ADREN_MELEE, rollDitch } from './whiskey';
import { AIM_BIT, refillLoadout, weaponOf } from './player';
import { Nemesis, NemesisHost, NEMESIS_DMG_MUL } from './nemesis';
import { RunStage, SaloonReason, SaloonStage, StageHost } from './saloon';
import { announceDestruct, blastWorld, breachBuilding, bumpStones, shotHitsWorld, stepDestruct } from './destruct';
import { stepStoneFalls } from './stonefall';

/** The snapshot header's coarse state, kept for the HUD (sim/run.ts has the real phases). */
export const enum DirState { Intermission = 0, Wave = 1, Cleared = 2, Wipe = 3 }

/** A lingering gas cloud (engineer.gd _GasCloud): DoT through shields and armor, slows, hits everyone. */
export interface GasCloud { on: boolean; id: number; x: number; y: number; z: number; r: number; t: number; dps: number; owner: number }
export const MAX_GAS = 16;
/** Gas damage lands in quarter-second pulses so it isn't an event per tick. */
const GAS_PULSE = 15;
/** Hazard element ids on the wire (Ev.Hazard), matching the renderer's El: 7 = gas cloud. */
export const HAZ_GAS_EL = 7;
/** Grenade procs carried by explode(). */
export const enum Proc { None = 0, Ice = 1, Magnetic = 2 }

/** Shot geometry shared by server resolution and client prediction. */
export function shotRay(seed: number, p: PlayerState, tick: number, pellet: number, inp: { yaw: number; pitch: number; buttons?: number }, spreadMul = 1) {
  const w = weaponOf(p);
  const spread = ((inp.buttons ?? 0) & AIM_BIT ? w.adsSpreadDeg : w.spreadDeg) * spreadMul;
  const d = aimDir(inp.yaw, inp.pitch, { x: 0, y: 0, z: 0 });
  // deterministic spread cone: the client predicts the exact pellet the server rolls
  const r1 = rand01(seed, p.id, tick, pellet * 2 + 1), r2 = rand01(seed, p.id, tick, pellet * 2 + 2);
  const moving = Math.abs(p.vx) + Math.abs(p.vz) > 1 || !p.grounded ? 1.6 : 1;
  const ang = (spread * moving * Math.PI / 180) * Math.sqrt(r1);
  const rot = r2 * Math.PI * 2;
  // basis perpendicular to d
  let ux = -d.z, uy = 0, uz = d.x;
  let ul = Math.sqrt(ux * ux + uz * uz);
  if (ul < 1e-4) { ux = 1; uz = 0; ul = 1; }
  ux /= ul; uz /= ul;
  const vx = d.y * uz - d.z * uy, vy = d.z * ux - d.x * uz, vz = d.x * uy - d.y * ux;
  const s = Math.sin(ang), c = Math.cos(ang);
  const cr = Math.cos(rot), sr = Math.sin(rot);
  const dx = d.x * c + (ux * cr + vx * sr) * s;
  const dy = d.y * c + (uy * cr + vy * sr) * s;
  const dz = d.z * c + (uz * cr + vz * sr) * s;
  // shots leave from the eye line, a touch right of centre (over-the-shoulder camera)
  return { ox: p.x, oy: p.y + EYE_HEIGHT, oz: p.z, dx, dy, dz };
}

/** Ev.Shot flags (field 11) */
export const enum ShotFlag { None = 0, Overload = 1, Tracked = 2, /** the Duel Blaster: the round is a bolt (ordnance), this event is only its muzzle */ Bolt = 4 }

/** Should this shot's rounds track? (Starter, Akimbo, a fully charged Duel Blaster) */
export function shotTracks(w: WeaponDef, mod: number) {
  return w.track > 0 && (w.perk !== Perk.PlasmaLock || mod === ShotMod.Locked);
}

export interface TrackCand { x: number; y: number; z: number; type: number }
/** Tracking rounds: bend the ray onto the body of the robot nearest the crosshair inside the cone,
 *  if nothing solid is in the way. Shared by the server and the client's predicted tracer.
 *  Returns the candidate's index, or -1 (the round flies where it was aimed). */
export function trackRay(r: { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number }, cone: number, range: number,
  cands: ArrayLike<TrackCand>, n: number, world: World, hit: RayHit): number {
  let best = -1, bestCos = Math.cos(cone * Math.PI / 180), tx = 0, ty = 0, tz = 0;
  for (let i = 0; i < n; i++) {
    const c = cands[i];
    if (isBossType(c.type)) continue; // a twenty-metre dog is hard to miss; its parts want real aim
    const d = ENEMIES[c.type];
    if (!d) continue;
    const bx = c.x - r.ox, by = c.y + d.bodyY - r.oy, bz = c.z - r.oz;
    const dist = Math.sqrt(bx * bx + by * by + bz * bz);
    if (dist > range || dist < 0.5) continue;
    const cos = (bx * r.dx + by * r.dy + bz * r.dz) / dist;
    if (cos <= bestCos) continue;
    world.raycast(r.ox, r.oy, r.oz, bx / dist, by / dist, bz / dist, dist, hit);
    if (hit.kind !== 0 && hit.t < dist - d.radius) continue; // behind cover: no curving round the rock
    best = i; bestCos = cos; tx = bx / dist; ty = by / dist; tz = bz / dist;
  }
  if (best >= 0) { r.dx = tx; r.dy = ty; r.dz = tz; }
  return best;
}

/** Godot health.gd armor_multiplier_for: above the armour full damage, at it AP_EQUAL_MUL, under it AP_CHIP_MUL (and the round bounces) */
/** one straight piece of a dropping round's arc (ARMOUR.md "bullet drop"): start, direction, length, path length before it */
export interface ShotSeg { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number; len: number; s0: number }
/** Chop a round's path into straight segments under BULLET_GRAVITY at `muzzleV` (0 = no drop: one straight segment).
 *  Fills `out` (pooled) and returns the count. Both ends run the same arc, so the predicted tracer lands where the server's does. */
export function dropSegments(r: { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number }, range: number, muzzleV: number, out: ShotSeg[]): number {
  const seg = (i: number) => out[i] ?? (out[i] = { ox: 0, oy: 0, oz: 0, dx: 0, dy: 0, dz: 0, len: 0, s0: 0 });
  if (muzzleV <= 0) { const o = seg(0); o.ox = r.ox; o.oy = r.oy; o.oz = r.oz; o.dx = r.dx; o.dy = r.dy; o.dz = r.dz; o.len = range; o.s0 = 0; return 1; }
  let n = 0, px = r.ox, py = r.oy, pz = r.oz, s0 = 0;
  for (let s = DROP_FREE; ; s += DROP_SEG) {
    const sc = Math.min(s, range), t = Math.max(0, sc - DROP_FREE) / muzzleV; // flat to the aim range (one piece), sagging past it (Godot's solved sag)
    const qx = r.ox + r.dx * sc, qy = r.oy + r.dy * sc - 0.5 * BULLET_GRAVITY * t * t, qz = r.oz + r.dz * sc;
    const ex = qx - px, ey = qy - py, ez = qz - pz, l = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1e-6;
    const o = seg(n++);
    o.ox = px; o.oy = py; o.oz = pz; o.dx = ex / l; o.dy = ey / l; o.dz = ez / l; o.len = l; o.s0 = s0;
    px = qx; py = qy; pz = qz; s0 += l;
    if (sc >= range) break;
  }
  return n;
}

export function apMultiplier(ap: number, av: number) {
  if (av <= 0) return 1; // unarmoured: every round is a round
  const diff = av - ap;
  if (diff < 0) return 1;
  if (diff === 0) return AP_EQUAL_MUL;
  return AP_CHIP_MUL; // chip: nobody is ever truly locked out, but the round is going somewhere else
}

/** Distance falloff for a weapon (falloffStart → falloffEnd, down to falloffMin). */
export function falloff(w: WeaponDef, dist: number) {
  if (dist <= w.falloffStart) return 1;
  if (dist >= w.falloffEnd) return w.falloffMin;
  return 1 - (1 - w.falloffMin) * (dist - w.falloffStart) / (w.falloffEnd - w.falloffStart);
}

export function primeDuration(maxHp: number) {
  if (maxHp >= 300) return -1; // heavies stay primed until detonated
  if (maxHp < 60) return secs(5);
  return secs(8.75);
}

/** breathing room after landing (and after a wipe) before the first robots arrive */
export const FIRST_WAVE_DELAY = 20;
/** robots never spawn closer than this to a live cowboy (m) */
export const SPAWN_MIN_DIST = 24;

/** posse lives scale with who's riding: 4 + posse size, never more than this */
export const POSSE_LIVES_MAX = 8;

/** the robot's armour tier now: the acid strips stripN tiers while stripT runs (sim/elements.ts) */
const armorOf = (e: Enemy) => Math.max(0, e.armor - (e.stripT > 0 ? e.stripN : 0));

/** an L3+ gun leaves a hazard on a hit this big, at most every HAZ_GUN_CD (ELEMENTS.md) */
const HAZ_GUN_DMG = 20, HAZ_GUN_CD = secs(3);
/** the hexed strongbox: reach, and the hold (Godot PICKUP_RADIUS 1.8, PICKUP_HOLD_SEC 0.8) */
const STRONGBOX_R = 1.8, STRONGBOX_HOLD = secs(0.8), CACHE_HOLD = secs(0.4);

export class Game implements AiHost, CritterHost, NemesisHost, StageHost {
  world: World;
  readonly seed: number;
  tick = 0;
  /** each cowboy's last bottle-ditch bit, so the next one is different */
  private ditchLast = new Map<number, number>();
  rng: Rng;
  players = new Map<number, PlayerState>();
  inputs = new Map<number, InputFrame>();
  /** older inputs to apply before `inputs` this tick (a client catching up after a late burst) */
  catchup = new Map<number, InputFrame[]>();
  enemies: Enemy[] = [];
  bullets = new Map<number, Bullet>();
  events: GameEvent[] = [];
  lives = POSSE_LIVES;
  readonly nemesis = new Nemesis();
  /** islands, waves, the gate, the boss and extraction (sim/run.ts) */
  run: RunState = newRun();
  /** the boss fight on this island, if any (sim/bosses: g.boss.kind picks its brain) */
  boss: BossBase | null = null;
  critters: Critter[] = [];
  /** the biggest the posse has been this run (lives are granted only past it) */
  private peakPosse = 0;
  private readonly away = new Set<number>();
  frozen = false;
  private nextEnemyId = 1;
  private nextBulletId = 1;
  private specialHits = new Map<number, Set<number>>();
  /** bullet drop: the current round's arc pieces and the segment ray being tested (pooled) */
  private segs: ShotSeg[] = [];
  private segRay = { ox: 0, oy: 0, oz: 0, dx: 0, dy: 0, dz: 0 };
  private exNone: { t: number; e: Enemy | null; kind: number } = { t: -1, e: null, kind: 0 };
  /** the robots a cowboy's step can't walk through this tick (pooled; see solidsFor) */
  private solidPool: Solid[] = [];
  private solidView: Solid[] = [];
  /** Juggernaut: the shield fraction the running Charge burnt (its damage scales with it) */
  private chargeF = new Map<number, number>();
  /** detonations so far (a shot that adds none breaks a Phantom's cloak) */
  private detonations = 0;
  /** preallocated: opening a cloud never allocates */
  /** every cowboy's camp clock: how the robots answer a perch (sim/perch.ts) */
  perch: PerchBook = new Map();
  /** the elemental hazards on the ground (sim/elements.ts): grenades leave them, the element that beats one reacts with it */
  hazards: Hazard[] = [];
  /** an L3+ gun's big hit leaves a hazard, at most one every HAZ_GUN_CD per cowboy */
  private hazAt = new Map<number, number>();
  /** Posse Repair Kit: feathers the whole posse shares, one a village per kit held */
  sharedWatch = 0;
  /** the run's mutators (MUTATORS.md): a bit per defs.ts Mut, rolled by sim/run.ts startRun */
  muts = 0;
  mut(m: number) { return (this.muts >> m) & 1; }
  /** the hexed strongbox (CARDS.md): one per island at most; every cowboy opens their own (bit per pid) */
  strongbox: { x: number; y: number; z: number; openedMask: number; holdT: number[] } | null = null;
  /** elemental barrels (Godot elemental_barrel.gd): placed by the buildings; a shot, a blast or a melee hit sets one off; a
   *  cluster's size is the element's level; a blast chains the next (on a short fuse) */
  barrels: { id: number; el: number; x: number; y: number; z: number; hp: number; level: number; fuse: number; by: number; dead: boolean }[] = [];
  /** the munitions cache (Godot munitions_cache.gd): hold Interact 0.4 s to level your grenade element, or refill the belt at L3 */
  cache: { x: number; y: number; z: number; mask: number; holdT: number[] } | null = null;
  /** cowboys who opened the strongbox and owe a Rare draft after the curse */
  private hexRare = new Set<number>();
  gas: GasCloud[] = Array.from({ length: MAX_GAS }, () => ({ on: false, id: 0, x: 0, y: 0, z: 0, r: 0, t: 0, dps: 0, owner: -1 }));
  /** grenades, ordnance, turrets, canopies, crates (sim/arsenal.ts) */
  readonly arsenal: Arsenal;
  /** power-up drops and the held-slot buffs (sim/powerups.ts) */
  readonly powerups: PowerUps;
  private usePressed = new Map<number, boolean>();
  private nextHazardId = 1;
  private stepOut: StepOut = newStepOut();
  private hit: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };
  /** called on wipe so the server can broadcast a fresh roster etc. */
  onWipe?: () => void;
  /** set by the saloon: called once the end card has played; the run then idles until startRun */
  onRunEnd?: (result: 'extract' | 'wipe') => void;
  private bossHit: BossHit = newBossHit();
  private critHit = { t: 0 };
  /** the saloon car and the run stage around it (sim/saloon.ts) */
  readonly saloon: SaloonStage;
  /** which island the saloon's launch drops the posse on (local dev: EMG_START_ISLAND) */
  startIsland = 0;
  /** the contract the posse signed in the saloon for its next ride (sim/progression.ts; NO_CONTRACT = none) */
  contract: number = NO_CONTRACT;
  /** local dev / lab: every boss island holds this boss (EMG_BOSS=cube; BossKind.None = the rotation) */
  devBoss: BossKind = BossKind.None;
  /** local dev: the launch drops on the bonus island / lays every power-up by the spawn */
  startBonus = false;
  devPowerups = false;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    this.world = new World(this.seed);
    this.rng = new Rng(hash32(this.seed, 0xd1ec));
    this.arsenal = new Arsenal(this);
    this.lives = this.posseLives();
    this.powerups = new PowerUps(this);
    this.saloon = new SaloonStage(this);
    // the run's end card hands the posse back to the car: the hotdog scene after a wipe, the bar after a getaway
    this.onRunEnd = (result) => this.saloon.returnToSaloon(result === 'extract' ? SaloonReason.Extract : SaloonReason.Wipe);
  }

  /** Straight onto the island with no saloon or launch (tests, bot soaks). The real path is the saloon's
   *  ready-up -> launch -> beginRun. */
  startRun(island = 0, bonus = false) { this.startIsland = island; this.startBonus = bonus; this.saloon.startNow(); }

  // ------------------------------------------------------------------ run stage hooks (StageHost)
  /** The launch is over: start the run state machine (sim/run.ts) on its first island. */
  beginRun() {
    this.peakPosse = this.players.size;
    startRun(this, this.startIsland, this.startBonus);
    if (this.devPowerups) {
      const sp = this.world.spawnPoint(0);
      for (let t = 1; t < PU_COUNT; t++) { const a = (t / (PU_COUNT - 1)) * Math.PI * 2; this.powerups.drop(sp.x + Math.cos(a) * 5, sp.z + Math.sin(a) * 5, 0, t / PU_COUNT, t); }
    }
  }

  /** Swap the island (sim/run.ts enterIsland). */
  setWorld(w: World) { this.world = w; }

  /** Coarse director view for the snapshot header and HUD. */
  get dir() {
    const r = this.run;
    const state = r.phase === RunPhase.Waves ? DirState.Wave
      : r.phase === RunPhase.Ended ? (r.result === RunResult.Extracted ? DirState.Intermission : DirState.Wipe)
      : r.phase === RunPhase.GateOpen || r.phase === RunPhase.Warping ? DirState.Cleared
      : r.phase === RunPhase.Boss || r.phase === RunPhase.BossIntro || r.phase >= RunPhase.ExtractBell ? DirState.Wave
      : DirState.Intermission;
    return { state, wave: r.wave, tier: runTier(r), timer: r.timer };
  }

  /** Clear whatever the last run left on the island (robots, bullets, hazards, ordnance). */
  clearRun() {
    this.enemies = [];
    this.bullets.clear();
    for (const g of this.gas) g.on = false;
    this.hazards.length = 0; this.strongbox = null; this.cache = null; this.barrels = [];
    this.arsenal.reset();
    this.boss = null;
    this.critters = [];
    this.powerups.reset();
  }

  emit(t: Ev, f: number[], s?: string) {
    this.events.push({ t, f, s });
  }

  addPlayer(id: number, slot: number, name: string, cls: ClassId) {
    const p = newPlayer(id, slot, name, cls, this.world);
    this.players.set(id, p);
    this.arsenal.announce();
    // a cowboy riding in brings a life with them, up to the posse's cap; only a posse bigger than
    // it has been this run counts, so joining and leaving again can't farm lives
    if (this.players.size > this.peakPosse) {
      this.peakPosse = this.players.size;
      const cap = this.posseLives();
      if (this.lives < cap) { this.lives = Math.min(cap, this.lives + 1); this.emit(Ev.Lives, [this.lives]); }
    }
    this.saloon.placeNew(p);
    return p;
  }

  /** Cowboys actually in the posse: connected and not out of the run. A pause still counts, so
   *  pausing through a breather can't shrink the next wave. */
  activePosse() {
    let n = 0;
    for (const p of this.players.values()) if (p.phase !== Phase.Dead && !this.away.has(p.id)) n++;
    return Math.max(1, n);
  }
  /** The room marks a cowboy whose connection dropped (they're held paused until they rejoin). */
  setAway(id: number, away: boolean) { if (away) this.away.add(id); else this.away.delete(id); }
  /** The shared life pool for this posse: 4 + posse size, capped (solo 5, four cowboys 8). */
  posseLives() { return Math.min(POSSE_LIVES_MAX, 4 + Math.max(1, this.players.size)); }
  /** Kit swaps only between waves, so a mid-fight swap can't refill a shield. */
  canSwapClass() { return this.dir.state !== DirState.Wave; }

  /** A downed cowboy lost the connection: spend the life now instead of leaving them on the ground. */
  forfeitDown(id: number) {
    const p = this.players.get(id);
    if (!p || p.phase !== Phase.Down) return;
    p.phaseT = 0;
    p.paused = 0; p.skyT = 0; // settle it here, they're lifted to safety again on the next tick
    this.downedTick(p);
  }

  // NemesisHost
  emitNemesis(eid: number, nameIdx: number, type: EType, victim: number) { this.emit(Ev.Nemesis, [eid, nameIdx, type, victim]); }
  notice(text: string) { this.emit(Ev.Notice, [], text); }
  queueSpawn(type: EType, at: number, nemesis: boolean) {
    // same 24 m rule as the waves; between waves the run timer is the intermission still to go
    const pts = clearSpawns(this);
    const sp = pts[this.rng.int(0, pts.length - 1)];
    this.run.queue.push({ at: at + this.run.timer, type, x: sp.x, z: sp.z, warned: false, nemesis });
  }
  removePlayer(id: number) {
    this.players.delete(id);
    this.away.delete(id);
    this.inputs.delete(id);
    this.saloon.remove(id);
    for (const e of this.enemies) if (e.target === id) e.target = -1;
  }
  setClass(id: number, cls: ClassId) {
    const p = this.players.get(id);
    if (!p) return;
    p.cls = cls;
    const c = CLASSES[cls];
    p.shield = Math.min(p.shield, c.shieldMax);
    refillLoadout(p); p.reloadT = 0; p.heat = Math.min(p.heat, c.heatMax * 0.5);
  }
  /** Saloon loadout: primary, secondary, grenade type. Any class can carry any gun. */
  /** Loadouts are picked in the saloon only (James, 2026-10-21): while the posse is in the saloon car
   *  (sim/saloon.ts stage), or once on joining for a drop-in cowboy. */
  loadoutOpen(p: PlayerState) { return this.saloon.stage !== RunStage.Run || !this.loadoutSet.has(p.id); }
  private loadoutSet = new Set<number>();
  setLoadout(id: number, l: Partial<Loadout>) {
    const p = this.players.get(id);
    if (!p || !this.loadoutOpen(p)) return;
    this.loadoutSet.add(id);
    const v = validLoadout(l, p.cls as ClassId);
    if (v.primary === p.w0 && v.secondary === p.w1 && v.grenade === p.nade) return;
    // only what changed comes fresh: re-picking the same kit mid-fight is not a free refill
    const nades = v.grenade === p.nade ? p.nades : GRENADE_MAX;
    if (p.wSlot === 0) p.mag0 = p.mag; else if (p.wSlot === 1) p.mag1 = p.mag;
    const changed0 = v.primary !== p.w0, changed1 = v.secondary !== p.w1;
    p.w0 = v.primary; p.w1 = v.secondary; p.nade = v.grenade; p.nades = nades;
    if (changed0) p.mag0 = weaponDef(p.w0).mag;
    if (changed1) p.mag1 = weaponDef(p.w1).mag;
    if (p.wSlot !== 2) { p.wSlot = 0; p.mag = p.mag0; }
    p.reloadT = 0; p.chargeT = 0; p.burstN = 0;
  }

  // ------------------------------------------------------------------ the tick
  step() {
    this.tick++;
    // in the saloon or mid-launch nothing on the island runs
    if (this.saloon.step()) { this.frozen = false; return; }
    const active = [...this.players.values()];
    // the whole world stops only when every cowboy is paused (MULTIPLAYER_DESIGN pause rules)
    this.frozen = active.length > 0 && active.every((p) => p.paused === 1 && p.skyT >= 1) && this.run.phase !== RunPhase.Ended;

    for (const p of active) {
      // a player whose input hasn't arrived simply doesn't advance this tick; their inputs are
      // applied in order when they land (never a guessed repeat), so prediction never diverges
      const extra = this.catchup.get(p.id);
      if (extra) for (const e of extra) this.applyInput(p, e);
      const inp = this.inputs.get(p.id);
      if (inp) this.applyInput(p, inp);
    }
    this.catchup.clear();
    this.stepRevives(active);

    if (this.frozen) return;
    this.powerups.step(this.usePressed);

    updatePerches(this.perch, this.players, this.world, this.tick);
    for (const e of this.enemies) {
      if (e.dead) continue;
      if (isBossType(e.type)) { if (this.boss) bossDef(this.boss.kind).stepEnemy?.(this, this.boss, e); if (e.hp <= 0) this.killEnemy(e, e.lastHitBy); }
      else stepEnemy(e, this);
    }
    if (this.boss) bossDef(this.boss.kind).step?.(this, this.boss);
    if (this.enemies.some((e) => e.dead)) this.enemies = this.enemies.filter((e) => !e.dead);
    for (const e of this.enemies) recordHistory(e, this.tick);
    this.recordPosse();

    this.stepBullets();
    this.stepGas();
    stepHazards(this, this.hazards);
    this.stepStrongbox();
    this.stepCache();
    this.stepBarrels();
    if (this.run.started) stepCritters(this.critters, this, this.rng);
    this.arsenal.step();
    stepDestruct(this);
    stepStoneFalls(this);
    stepRun(this);
  }

  /** The robots near a cowboy, where he saw them (lag-compensated), as solids his step can't pass through.
   *  Bosses are left out: their bodies are hit parts, not one cylinder. */
  private solidsFor(p: PlayerState, inp: InputFrame): readonly Solid[] {
    if (p.paused || p.skyT > 0 || p.move === Move.Special) return this.solidView.length ? (this.solidView.length = 0, this.solidView) : this.solidView;
    const view = clamp(inp.viewTick || this.tick, this.tick - LAG_COMP_MAX_TICKS, this.tick);
    const hp = this.solidHist;
    let n = 0;
    for (const e of this.enemies) {
      if (e.dead || isBossType(e.type) || e.st === EState.Spawn) continue;
      historyAt(e, view, this.tick, hp);
      if (Math.abs(hp.x - p.x) > 5 || Math.abs(hp.z - p.z) > 5 || Math.abs(hp.y - p.y) > 6) continue;
      const d = ENEMIES[e.type];
      const s = this.solidPool[n] ?? (this.solidPool[n] = { x: 0, z: 0, y0: 0, y1: 0, r: 0 });
      s.x = hp.x; s.z = hp.z; s.y0 = hp.y; s.y1 = hp.y + d.headY + 0.2; s.r = d.radius;
      n++;
    }
    this.solidView.length = n;
    for (let i = 0; i < n; i++) this.solidView[i] = this.solidPool[i];
    return this.solidView;
  }
  private solidHist = { x: 0, y: 0, z: 0, yaw: 0 };

  /** A cowboy rode in mid-island: what's burnt and what's fallen (sim/destruct.ts). */
  announceWorld() { announceDestruct(this); }

  private applyInput(p: PlayerState, inp: InputFrame) {
    // aboard the extraction train: lifted out of the fight like a pause (the client does the same)
    if (isAboard(this, p.id)) inp.buttons |= Btn.Pause;
    this.usePressed.set(p.id, (inp.buttons & Btn.PowerUp) !== 0 && (p.prevButtons & Btn.PowerUp) === 0 && p.drinkT === 0);
    const wasSpecial = p.move === Move.Special;
    const out = stepPlayer(p, inp, this.world, this.stepOut, this.solidsFor(p, inp));
    if (out.shots) this.resolveShots(p, inp, out);
    if (out.fallDmg > 0) this.hurtPlayer(p, out.fallDmg, p.x, p.z, 0);
    if (out.breach >= 0) breachBuilding(this, out.breach, p, false);
    if (out.breachOut >= 0) breachBuilding(this, out.breachOut, p, true);
    if (out.bonk > 0) bumpStones(this, p, out.bonk, out.bonkX, out.bonkZ);
    if (out.bonk > 0) { const d = Math.min(BONK_MAX, (out.bonk - BONK_MIN) * BONK_DMG_PER_MS); this.hurtPlayer(p, d, p.x - p.vx, p.z - p.vz, 16, p.vx * 0.5, p.vz * 0.5, true); } // flew into something: flag 16 is the grunt; it hits the hull
    if (out.throw) { this.arsenal.throwGrenade(p, inp, out.cook); this.emit(Ev.Throw, [p.id, p.nade]); }
    if (out.cookOff) { this.arsenal.cookOff(p); this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()} COOKED IT TOO LONG`); }
    if (out.shove) this.arsenal.shove(p, weaponOf(p).emptyShove);
    if (out.drink) {
      // how he'll ditch the empty: rolled here so every screen shows the same bit (sim/whiskey.ts Ditch)
      const d = rollDitch(rand01(this.seed, this.tick, p.id, 0xd17c), this.ditchLast.get(p.id) ?? -1);
      this.ditchLast.set(p.id, d);
      this.emit(Ev.Drink, [p.id, p.bottles, d]);
    }
    if (out.melee) this.resolveMelee(p, inp);
    if (out.hammer >= 0) this.resolveHammer(p, inp, out.hammer);
    if (out.blink) this.resolveBlink(p, inp);
    if (out.poundLand) this.resolvePound(p);
    if (out.special) {
      this.specialHits.set(p.id, new Set()); this.emit(Ev.Special, [p.id]);
      // Shield Charge: the whole shield a Charge burns banks into the special weapon and grenades, like any shield
      // hit, and the dash hits as hard as the shield it spent (Godot's Shield Charge Loop)
      if (p.cls === ClassId.Juggernaut) { this.arsenal.onShieldDamage(p, out.shieldBurnt); this.chargeF.set(p.id, Math.min(1, out.shieldBurnt / CLASSES[p.cls].shieldMax)); }
      // the Phantom's warp: the sweep through robots on the line, and a hot pack's self-damage
      if (p.cls === ClassId.Phantom) {
        this.warpSweep(p, out.warpX, out.warpZ);
        if (out.warpSelf > 0) { p.hp -= out.warpSelf; if (p.hp <= 0) { p.hp = 1; } this.emit(Ev.PlayerHurt, [p.id, out.warpSelf, p.x, p.z, 0]); }
      }
    }
    if (p.move === Move.Special || wasSpecial) this.resolveSpecial(p);
    if (out.skySlam) this.slamAround(p, 6, 0, 200, 'DOWNDRAFT'); // the Valkyrie's Downdraft card
    if (wasSpecial && p.move !== Move.Special && p.cls === ClassId.Juggernaut && modsOf(p).jugSlow) {
      for (const e of this.enemies) if (!e.dead && len2(e.x - p.x, e.z - p.z) < 5 + ENEMIES[e.type].radius) e.slowT = Math.max(e.slowT, secs(4)); // Aftershock
    }
    // the bleed-out keeps running through a pause: the sky keeps you safe, it doesn't stop the clock
    if (p.phase === Phase.Down) this.downedTick(p);
  }

  // ------------------------------------------------------------------ player combat
  /** Pulse Rifle: rounds of the current burst that hit a robot, and which robot (Perk.Overload) */
  private burstHits = new Map<number, { n: number; eid: number }>();
  private trackCands: (TrackCand & { e: Enemy })[] = [];
  private resolveShots(p: PlayerState, inp: InputFrame, out: StepOut) {
    const w = weaponOf(p);
    // Smoke and Mirrors is brittle: any shot that doesn't detonate something gives the Phantom away
    const det0 = this.detonations;
    if (w.proj !== Proj.Hitscan) { if (p.cls === ClassId.Phantom) p.cloakT = 0; this.arsenal.fireOrdnance(p, inp, w); return; }
    const view = clamp(inp.viewTick || this.tick, this.tick - LAG_COMP_MAX_TICKS, this.tick);
    const tracks = shotTracks(w, out.mod);
    const pellets = w.pellets * (out.mod === ShotMod.Double ? 2 : 1);
    const flags = tracks ? ShotFlag.Tracked : ShotFlag.None;
    if (tracks) {
      // where each robot was on your screen, for the tracking cone
      const tc = this.trackCands;
      tc.length = 0;
      for (const e of this.enemies) {
        if (e.dead || isBossType(e.type) || (e.type === EType.Stalker && e.revealT === 0)) continue; // a cloaked Stalker can't be locked
        const h = { x: 0, y: 0, z: 0, yaw: 0, type: e.type, e };
        historyAt(e, view, this.tick, h);
        tc.push(h);
      }
    }
    // the Duel Blaster (Nick 2026-10-22, Godot blaster_pistol): slow plasma bolts that curve onto the robot nearest
    // the crosshair, in the tracking cone, instead of an instant ray. The bolt is ordnance from here on.
    if (w.id === W.DuelBlaster) {
      const r = shotRay(this.seed, p, inp.tick, 0, inp, out.spreadMul);
      const d0 = { ox: r.ox, oy: r.oy, oz: r.oz, dx: r.dx, dy: r.dy, dz: r.dz };
      const tc = this.trackCands; tc.length = 0;
      for (const e of this.enemies) { if (e.dead || isBossType(e.type) || (e.type === EType.Stalker && e.revealT === 0)) continue; const h = { x: 0, y: 0, z: 0, yaw: 0, type: e.type, e }; historyAt(e, view, this.tick, h); tc.push(h); }
      const ti = trackRay(d0, w.track, w.range, tc, tc.length, this.world, this.hit);
      const target = ti >= 0 ? tc[ti].e.id : -1; // the lock: whatever the cone finds, at any charge
      // twin bolts (Nick, playtest 2): one from each pistol, side by side, each carrying that pistol's element
      const rx = Math.cos(p.yaw), rz = -Math.sin(p.yaw);
      this.arsenal.spawnBolt(p, w, r.dx, r.dy, r.dz, target, out.mul, -0.22 * rx, -0.22 * rz, gunElem(p, 0), gunLevel(p, 0));
      this.arsenal.spawnBolt(p, w, r.dx, r.dy, r.dz, target, out.mul, 0.22 * rx, 0.22 * rz, gunElem(p, 1), gunLevel(p, 1));
      this.emit(Ev.Shot, [p.id, r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, 0.5, 0, 0, w.id, flags | ShotFlag.Bolt]);
      if (p.cls === ClassId.Phantom && p.cloakT > 0) p.cloakT = 0;
      return;
    }
    // point-blank stagger: pellets per robot this trigger pull
    const stag = w.perk === Perk.Stagger || out.mod === ShotMod.Double ? new Map<Enemy, number>() : null;
    let hitE: Enemy | null = null;
    for (let pellet = 0; pellet < pellets; pellet++) {
      const r0 = shotRay(this.seed, p, inp.tick, pellet, inp, out.spreadMul);
      const tracked = tracks && trackRay(r0, w.track, w.range, this.trackCands, this.trackCands.length, this.world, this.hit) >= 0;
      // bullet drop (ARMOUR.md): the round's arc as straight segments; tracked rounds bend on their own and fly flat
      const nSeg = dropSegments(r0, w.range, tracked ? 0 : w.muzzleV, this.segs);
      const r = this.segRay;
      let bestT = 0, bestE: Enemy | null = null, head = false, bestP: PlayerState | null = null, headP = false, along = 0;
      const hp = { x: 0, y: 0, z: 0, yaw: 0 };
      const base = w.damage * out.mul;
      let ex = this.exNone;
      for (let si = 0; si < nSeg; si++) {
      const sg = this.segs[si];
      r.ox = sg.ox; r.oy = sg.oy; r.oz = sg.oz; r.dx = sg.dx; r.dy = sg.dy; r.dz = sg.dz;
      this.world.raycast(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, sg.len, this.hit);
      bestT = this.hit.t; bestE = null; head = false; bestP = null; headP = false; along = sg.s0;
      for (const e of this.enemies) {
        if (e.dead || isBossType(e.type)) continue;
        const d = ENEMIES[e.type];
        historyAt(e, view, this.tick, hp); // server rewind: where you saw it
        // tracking rounds find the body, never the head: the precision bonus belongs to manual aim
        const th = tracked ? -1 : raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, hp.x, hp.y + d.headY, hp.z, d.headR * 1.15);
        if (th >= 0 && th < bestT) { bestT = th; bestE = e; head = true; }
        const tb = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, hp.x, hp.y + d.bodyY, hp.z, d.radius);
        if (tb >= 0 && tb < bestT - 0.05) { bestT = tb; bestE = e; head = false; }
      }
      // friendly fire is always on (James, 2026-10-21; Godot parity): teammates stop bullets too,
      // tracked rounds included: a round bending to a robot still hits the cowboy in the way
      for (const o of this.players.values()) {
        if (o === p || o.phase !== Phase.Alive || !isTargetable(o)) continue;
        const op = this.posseAt(o, view, hp); // server rewind, same as robots
        const th = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, op.x, op.y + PLAYER_HEIGHT - 0.2, op.z, 0.26);
        if (th >= 0 && th < bestT) { bestT = th; bestP = o; headP = true; bestE = null; }
        const tb = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, op.x, op.y + PLAYER_HEIGHT * 0.45, op.z, PLAYER_RADIUS + 0.05);
        if (tb >= 0 && tb < bestT - 0.05) { bestT = tb; bestP = o; headP = false; bestE = null; }
      }
      // the boss's parts, livestock and the extraction bell can stand in front of all that, in this piece of the arc
      ex = this.shotExtras(p, r, view, bestT, base, w.ap);
      if (ex.t >= 0 || bestT < sg.len - 1e-4 || this.hit.kind !== 0) break; // something in this piece of the arc
      }
      let kind = this.hit.kind !== 0 ? 1 : 0;
      if (ex.t >= 0) { bestT = ex.t; bestE = ex.e; kind = ex.kind; if (ex.e) hitE = ex.e; }
      else if (bestP) {
        const dmg = base * falloff(w, along + bestT) * (headP ? w.headMul : 1);
        this.friendlyHurt(bestP, p.id, dmg, p.x, p.z, r.dx * w.push * 0.1, r.dz * w.push * 0.1);
        kind = 2;
      } else if (bestE) {
        kind = head ? 3 : 2;
        const mh = modsOf(p);
        let dmg = base * falloff(w, along + bestT) * this.dealMul(p, mh) * this.gunHitMul(p, mh, along + bestT, armorOf(bestE));
        // the gun's element (sim/elements.ts): a touch softer until L4, x1.5 on a robot whose element it beats
        const gEl = gunElem(p, p.wSlot), gLvl = gunLevel(p, p.wSlot);
        dmg *= EL_DMG_MOD[gLvl] * weaknessMul(bestE, gEl) * gunLevelDmg(p, p.wSlot) * (p.acidBoostT > 0 && bestE.armor > 0 ? 1.1 : 1); // Gunsmith; Conduit: Acid
        const hpBefore = bestE.hp + bestE.shield;
        if (w.shieldMul !== 1 && bestE.shield > 0) {
          // plasma: shields take shieldMul x; whatever is left over lands at the normal rate
          const vsShield = dmg * w.shieldMul;
          dmg = vsShield <= bestE.shield ? vsShield : bestE.shield + (vsShield - bestE.shield) / w.shieldMul;
        }
        // armour (ARMOUR.md): a round into its back is a weak-point hit that bypasses armour; a round that can't beat
        // its armour chips and RICOCHETS off (the spark, then the bounce)
        const bfx = -Math.sin(bestE.yaw), bfz = -Math.cos(bestE.yaw);
        const weak = !isBossType(bestE.type) && (bfx * r.dx + bfz * r.dz) > WEAK_POINT_DOT - 0.25 * bestE.shed; // each panel shed widens the back weak arc
        const av = armorOf(bestE);
        const chip = !weak && apMultiplier(w.ap, av) <= AP_CHIP_MUL;
        if (chip) kind = 4;
        const hx = r.ox + r.dx * bestT, hy = r.oy + r.dy * bestT, hz = r.oz + r.dz * bestT;
        this.shotHitsEnemy(p, bestE, head, r.dx, r.dz, dmg, w.ap, hx, hy, hz, w.headMul, weak);
        this.roundMods(p, mh, bestE, dmg, w.ap, hx, hy, hz, r.dx, r.dy, r.dz);
        if (mh.vampiric > 0) this.heal(p, Math.min(4, Math.max(0, hpBefore - (bestE.dead ? 0 : bestE.hp + bestE.shield)) * mh.vampiric)); // Vampiric, capped per round
        if (!chip) this.elemHit(p, bestE, dmg, hx, hy, hz);
        if (chip && w.shieldMul === 1) this.ricochet(p, w, hx, hy, hz, r.dx, r.dy, r.dz, bestE, hp.x, hp.y + ENEMIES[bestE.type].bodyY, hp.z, inp.tick, pellet);
        if (!bestE.dead && !isBossType(bestE.type)) {
          // every round shoves and stuns a beat, by how much of its hull it took (playtest 2); the gun's push rides on top
          const wt = ENEMIES[bestE.type].weight, frac = Math.min(1, dmg / bestE.maxHp);
          bestE.kx += r.dx * (w.push + HIT_SHOVE * frac * 4) / wt; bestE.kz += r.dz * (w.push + HIT_SHOVE * frac * 4) / wt;
          if (frac > 0.08 && true) bestE.stunT = Math.max(bestE.stunT, Math.min(ROBOT_HIT_STUN_MAX, Math.round(HIT_STUN_PER_FRAC * frac)));
        }
        if (!bestE.dead && !isBossType(bestE.type) && !this.mut(Mut.Unbreakable)) bestE.flinchT = FLINCH_TICKS; // every round lands with a hitch (not under UNBREAKABLE)
        hitE = bestE;
        if (stag && bestT <= STAGGER_RANGE) stag.set(bestE, (stag.get(bestE) ?? 0) + 1);
      }
      // a shot that hit the world may have hit a building (sim/destruct.ts)
      if (kind === 1 && this.hit.kind === 2 && bestT === this.hit.t) shotHitsWorld(this, r.ox + r.dx * bestT, r.oy + r.dy * bestT, r.oz + r.dz * bestT, w.damage * falloff(w, bestT), p.id);
      // the tracer: from the muzzle straight to where the round ended up (a dropped round lands low; the chord shows it)
      { const ex = r.ox + r.dx * bestT - r0.ox, ey = r.oy + r.dy * bestT - r0.oy, ez = r.oz + r.dz * bestT - r0.oz, el = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1e-6;
        this.emit(Ev.Shot, [p.id, r0.ox, r0.oy, r0.oz, ex / el, ey / el, ez / el, el, kind, bestE ? bestE.id : 0, w.id, flags]); }
    }
    if (stag) {
      const need = staggerPellets(w, out.mod);
      for (const [e, n] of stag) if (n >= need && !e.dead) e.stunT = Math.max(e.stunT, e.maxHp >= 300 ? secs(0.2) : STAGGER_STUN);
    }
    if (w.perk === Perk.Overload) this.overload(p, w, hitE);
    if (p.cls === ClassId.Phantom && p.cloakT > 0 && this.detonations === det0) p.cloakT = 0;
    // Hot Shot is one loaded bullet: a trigger pull in the chain window that detonates nothing (a miss, a body shot) ends the chain
    if (p.cls === ClassId.Gunslinger && p.chainT > 0 && this.detonations === det0) {
      const m = modsOf(p);
      if (m.chainMiss > 0 && !p.chainMissUsed) p.chainMissUsed = 1; // Second Wind: one miss forgiven per chain
      else if (!(m.hotshotBodyOk && hitE)) { p.chainT = 0; p.chainN = 0; } // Steady Hands: a body shot keeps it
    }
    if (this.critters.length) critterNoise(this.critters, p.x, p.z);
  }

  /** Pulse Rifle: three for three on robots and a fourth pulse follows into the last one, shocking it stiff. */
  private overload(p: PlayerState, w: WeaponDef, hitE: Enemy | null) {
    let b = this.burstHits.get(p.id);
    if (!b) { b = { n: 0, eid: 0 }; this.burstHits.set(p.id, b); }
    const round = w.burst - 1 - p.burstN; // 0 = the burst's first round
    if (round === 0) b.n = 0;
    if (hitE) { b.n++; b.eid = hitE.id; }
    if (round !== w.burst - 1) return;
    if (b.n < w.burst) { b.n = 0; return; }
    b.n = 0;
    const e = this.enemies.find((x) => x.id === b!.eid && !x.dead);
    if (!e) return;
    const d = ENEMIES[e.type];
    const ox = p.x, oy = p.y + EYE_HEIGHT, oz = p.z;
    const hx = e.x, hy = e.y + d.bodyY, hz = e.z;
    let dx = hx - ox, dy = hy - oy, dz = hz - oz;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    dx /= dist; dy /= dist; dz /= dist;
    this.shotHitsEnemy(p, e, false, dx, dz, OVERLOAD_DMG, w.ap, hx, hy, hz, 1);
    if (!e.dead) e.stunT = Math.max(e.stunT, isBossType(e.type) || e.maxHp >= 300 ? secs(0.15) : OVERLOAD_STUN);
    this.emit(Ev.Shot, [p.id, ox, oy, oz, dx, dy, dz, dist, 3, e.id, w.id, ShotFlag.Overload]);
  }

  private exOut = { t: -1, e: null as Enemy | null, kind: 0 };
  /** Boss parts, critters and the bell. Applies the hit itself; t = -1 if none was nearer. */
  private shotExtras(p: PlayerState, r: { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number }, view: number, maxT: number, dmg: number, ap: number) {
    const o = this.exOut;
    o.t = -1; o.e = null; o.kind = 0;
    let best = maxT;
    // the boss (sim/bosses): rewound like any robot, then tested part by part
    const b = this.boss;
    let bossE: Enemy | null = null;
    if (b) {
      const bh = bossDef(b.kind).raycast(this, b, r, view, best, this.bossHit);
      if (bh.t >= 0 && bh.e) { best = bh.t; bossE = bh.e; }
    }
    // livestock
    const ci = this.critters.length ? critterRaycast(this.critters, r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, best, this.critHit) : -1;
    if (ci >= 0) { best = this.critHit.t; bossE = null; }
    // the bell
    let bell = false;
    const rp = this.run.phase;
    if (rp >= RunPhase.ExtractBell && rp <= RunPhase.ExtractBoard) {
      const bp = bellPos(this.world);
      const t = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, bp.x, bp.y, bp.z, BELL_R);
      if (t >= 0 && t < best) { best = t; bell = true; bossE = null; }
    }
    // the gun routers on Shooters and Snipers (weapon_sever.gd): a small sphere on the gun arm, tested with a little slack
    // so a round that would have hit the body right there goes to the gun instead; a barrel is a target too
    let router: Enemy | null = null, barrel = -1;
    const hp = { x: 0, y: 0, z: 0, yaw: 0 };
    for (const e of this.enemies) {
      if (e.dead || e.gunHp <= 0 || (e.type !== EType.Shooter && e.type !== EType.Sniper)) continue;
      historyAt(e, view, this.tick, hp);
      const gx = hp.x + Math.cos(hp.yaw) * 0.55 - Math.sin(hp.yaw) * 0.3, gz = hp.z - Math.sin(hp.yaw) * 0.55 - Math.cos(hp.yaw) * 0.3;
      const t = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, gx, hp.y + ENEMIES[e.type].bodyY + 0.1, gz, SEVER_R);
      if (t >= 0 && t <= best + 0.35 && (router === null || t < best)) { best = Math.min(best, t); router = e; bossE = null; bell = false; }
    }
    for (let i = 0; i < this.barrels.length; i++) {
      const b = this.barrels[i];
      if (b.dead) continue;
      const t = raySphere(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, b.x, b.y + 0.5, b.z, BARREL_RADIUS + 0.1);
      if (t >= 0 && t < best) { best = t; barrel = i; router = null; bossE = null; bell = false; }
    }
    const hx = r.ox + r.dx * best, hy = r.oy + r.dy * best, hz = r.oz + r.dz * best;
    if (barrel >= 0) { this.hitBarrel(this.barrels[barrel], dmg, p.id); o.t = best; o.kind = 1; return o; }
    if (router) {
      router.gunHp -= dmg;
      this.damageEnemy(router, dmg * SEVER_FORWARD, ap, p.id, 0, hx, hy, hz);
      this.emit(Ev.EnemyHit, [router.id, Math.round(dmg), 4, p.id, hx, hy, hz]);
      if (router.gunHp <= 0 && !router.dead) { router.gunHp = 0; router.parts |= 1 << Part.RightArm; this.emit(Ev.PartPop, [router.id, Part.RightArm, hx, hy, hz]); this.emit(Ev.Stagger, [router.id]); this.emit(Ev.Notice, [], router.type === EType.Sniper ? 'RIFLE SHOT OFF: THE SNIPER IS DONE SHOOTING' : 'GUN ARMS SHOT OFF: IT CAN ONLY CLAW NOW'); }
      o.t = best; o.e = router; o.kind = 3; return o;
    }
    if (bell) { ringBell(this, p.id); o.t = best; o.kind = 1; return o; }
    if (ci >= 0) {
      const c = this.critters[ci];
      const killed = hurtCritter(this.critters, c, dmg, p.id, p.x, p.z);
      this.emit(Ev.Critter, [c.id, killed ? 1 : 0, p.id]);
      if (killed) this.emit(Ev.Notice, [], c.type === 0 ? `G.U.L.P. HAS BILLED ${p.name.toUpperCase()} FOR ONE (1) COW.` : `${p.name.toUpperCase()} TOOK DOWN THE ${CRITTERS[c.type].name.toUpperCase()}.`);
      o.t = best; o.kind = 2; return o;
    }
    if (bossE && b) {
      const ph = this.bossHit;
      const def = bossDef(b.kind);
      // the boss uses its part table, not the gun's head bonus
      const base = dmg * ph.mul;
      const hpBefore = bossE.hp;
      const armor = bossE.armor;
      bossE.armor = ph.av;
      this.shotHitsEnemy(p, bossE, ph.head, r.dx, r.dz, base, ap, hx, hy, hz, 1);
      bossE.armor = armor;
      def.tookHit(this, b, ph, Math.max(0, hpBefore - bossE.hp));
      if (def.procs && !bossE.dead) this.elemHit(p, bossE, dmg);
      o.t = best; o.e = bossE; o.kind = ph.weak ? 3 : ph.av > ap ? 4 : 2;
      return o;
    }
    return o;
  }

  // ------------------------------------------------------------------ boss host
  /** a proc landed on a robot: the boss reads its own elements (arsenal.proc) */
  procd(e: Enemy, elem: number, owner: number) {
    if (this.boss && isBossType(e.type)) bossDef(this.boss.kind).proc?.(this, this.boss, e, elem, owner);
  }

  bossEvent(kind: BossEv, a: number, c: number) {
    const b = this.boss;
    this.emit(Ev.Boss, [b ? b.eid : 0, kind, a, c]);
    const note = b ? bossDef(b.kind).note?.(b, kind, a, c) : undefined;
    if (note) this.emit(Ev.Notice, [], note);
  }

  spawnAdds(n: number) {
    const ar = this.world.arena;
    for (let i = 0; i < n; i++) {
      const a = this.rng.range(0, Math.PI * 2), r = this.rng.range(26, 34);
      const x = ar.x + Math.cos(a) * r, z = ar.z + Math.sin(a) * r;
      if (this.world.terrain(x, z) < -1e8) continue;
      this.emit(Ev.SpawnWarn, [x, this.world.support(x, z, 60), z, EType.Swarmer, secs(1.2)]);
      this.spawnEnemy(i === 3 ? EType.Shooter : EType.Swarmer, x, z);
    }
  }

  // ---- friendly fire lag compensation: where each cowboy stood over the last HISTORY_TICKS
  private posseHist = new Map<number, { x: Float32Array; y: Float32Array; z: Float32Array }>();
  private recordPosse() {
    const i = this.tick % HISTORY_TICKS;
    for (const p of this.players.values()) {
      let h = this.posseHist.get(p.id);
      if (!h) {
        h = { x: new Float32Array(HISTORY_TICKS).fill(p.x), y: new Float32Array(HISTORY_TICKS).fill(p.y), z: new Float32Array(HISTORY_TICKS).fill(p.z) };
        this.posseHist.set(p.id, h);
      }
      h.x[i] = p.x; h.y[i] = p.y; h.z[i] = p.z;
    }
    if (this.posseHist.size > this.players.size) for (const id of this.posseHist.keys()) if (!this.players.has(id)) this.posseHist.delete(id);
  }
  /** a teammate where the shooter saw them (remote cowboys are drawn at the same view tick as robots) */
  private posseAt(p: PlayerState, tick: number, out: { x: number; y: number; z: number }) {
    const h = this.posseHist.get(p.id);
    if (!h || tick >= this.tick || this.tick - tick >= HISTORY_TICKS - 1) { out.x = p.x; out.y = p.y; out.z = p.z; return out; }
    const t0 = Math.floor(tick), f = tick - t0, a = t0 % HISTORY_TICKS, b = (t0 + 1) % HISTORY_TICKS;
    out.x = h.x[a] + (h.x[b] - h.x[a]) * f; out.y = h.y[a] + (h.y[b] - h.y[a]) * f; out.z = h.z[a] + (h.z[b] - h.z[a]) * f;
    return out;
  }

  private shotHitsEnemy(p: PlayerState, e: Enemy, head: boolean, dx: number, dz: number, base: number, ap: number, hx: number, hy: number, hz: number, headMul: number, weak = false) {
    let dmg = base * (head ? headMul : 1);
    this.wearPart(e, dmg, hx, hy, hz);
    if (p.cls === ClassId.Gunslinger && p.chainT > 0) { const m = modsOf(p); if (head) dmg *= m.hotshotHeadMul; else if (m.hotshotBodyZero) dmg = 0; } // Dead Eye's Debt
    const fwdX = -Math.sin(e.yaw), fwdZ = -Math.cos(e.yaw);
    const fromBehind = fwdX * dx + fwdZ * dz > 0.35;
    const airborne = !p.grounded;
    const primed = e.primedBy >= 0;
    let detonate = false;
    if (primed) {
      switch (p.cls) {
        case ClassId.Gunslinger:
          if (head) detonate = true;
          else if (!modsOf(p).hotshotBodyOk) { e.primedBy = -1; this.emit(Ev.Unprime, [e.id]); } // body shot wastes the setup (Steady Hands forgives it)
          break;
        case ClassId.Valkyrie: detonate = airborne; break;
        // from behind, from the smoke, or while it's still reeling from the backstab (Godot: a stunned robot
        // can't see you, so it counts as "out of its sight lines"; that's the beat that makes the shot lineable)
        case ClassId.Phantom:
          detonate = fromBehind || p.cloakT > 0 || e.stunT > 0;
          // a shot on a primed robot from the wrong angle wastes the prime, closes the chain and gives you away (Godot)
          if (!detonate) { e.primedBy = -1; this.emit(Ev.Unprime, [e.id]); p.chainT = 0; p.chainN = 0; p.cloakT = 0; }
          break;
        case ClassId.Juggernaut: detonate = false; break;
      }
    } else if (p.cls === ClassId.Gunslinger && p.chainT > 0 && head && e.shield <= 0) {
      // Hot Shot: in the chain window any headshot primes and detonates
      e.primedBy = p.id; detonate = true;
    }
    if (detonate && this.detonate(e, p, dmg)) return;
    this.damageEnemy(e, dmg, ap, p.id, head ? 1 : 0, hx, hy, hz, weak);
  }

  /** A round that glanced off armour bounces: reflected off the robot's hull, kicked up a little with a hashed
   *  jitter, slower (RICOCHET_RANGE), and whatever is in its way next takes RICOCHET_FRAC of the gun's damage at
   *  the gun's AP. That includes cowboys, the shooter among them (Godot blaster_bolt.gd: the bounce drops its
   *  collision exception with you). Energy rounds (plasma) fizzle instead and never get here. */
  private ricochet(p: PlayerState, w: WeaponDef, x: number, y: number, z: number, dx: number, dy: number, dz: number, from: Enemy, cx: number, cy: number, cz: number, tick: number, salt: number) {
    let nx = x - cx, ny = y - cy, nz = z - cz;
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (nl < 1e-4) { nx = -dx; ny = -dy; nz = -dz; } else { nx /= nl; ny /= nl; nz /= nl; }
    const dot = dx * nx + dy * ny + dz * nz;
    let rx = dx - 2 * dot * nx, ry = dy - 2 * dot * ny, rz = dz - 2 * dot * nz;
    rx += (rand01(this.seed, tick, p.id, salt * 3 + 1) - 0.5) * 2 * RICOCHET_JITTER;
    rz += (rand01(this.seed, tick, p.id, salt * 3 + 2) - 0.5) * 2 * RICOCHET_JITTER;
    ry += RICOCHET_UP + (rand01(this.seed, tick, p.id, salt * 3 + 3) - 0.5) * RICOCHET_JITTER;
    const rl = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1;
    rx /= rl; ry /= rl; rz /= rl;
    // step off the hull so the bounce can't re-hit the robot it came off
    const ox = x + rx * 0.3, oy = y + ry * 0.3, oz = z + rz * 0.3;
    this.world.raycast(ox, oy, oz, rx, ry, rz, RICOCHET_RANGE, this.hit);
    let bestT = this.hit.t, kind = this.hit.t < RICOCHET_RANGE ? 1 : 0, hitE: Enemy | null = null, hitP: PlayerState | null = null;
    for (const e of this.enemies) {
      if (e.dead || e === from || isBossType(e.type)) continue;
      const d = ENEMIES[e.type];
      const t = raySphere(ox, oy, oz, rx, ry, rz, e.x, e.y + d.bodyY, e.z, d.radius);
      if (t >= 0 && t < bestT) { bestT = t; hitE = e; kind = 2; }
    }
    for (const q of this.players.values()) {
      if (!isTargetable(q) || q.phase !== Phase.Alive) continue;
      for (const h of [0.45, 0.95, 1.45]) {
        const t = raySphere(ox, oy, oz, rx, ry, rz, q.x, q.y + h, q.z, 0.5);
        if (t >= 0 && t < bestT) { bestT = t; hitP = q; hitE = null; kind = 5; }
      }
    }
    const dmg = w.damage * RICOCHET_FRAC;
    if (hitE) { this.damageEnemy(hitE, dmg, w.ap, p.id, 0, ox + rx * bestT, oy + ry * bestT, oz + rz * bestT); if (!hitE.dead) hitE.flinchT = FLINCH_TICKS; }
    else if (hitP) { if (hitP === p) this.hurtPlayer(p, dmg, ox, oz, 0); else this.friendlyHurt(hitP, p.id, dmg, ox, oz); }
    this.emit(Ev.Ricochet, [p.id, ox, oy, oz, rx, ry, rz, bestT, kind, hitE ? hitE.id : 0, w.id]);
  }

  /** Apply damage. Returns true if it killed. */
  damageEnemy(e: Enemy, amount: number, ap: number, by: number, flags: number, hx: number, hy: number, hz: number, bypassArmor = false, bypassShield = false) {
    if (e.dead) return false;
    let dmg = amount;
    if (e.vulnT > 0) dmg *= 1.25;
    if (e.staggerT > 0) dmg *= STAGGER_DMG_MUL; // reeling: wide open (Godot stagger_damage_multiplier)
    dmg *= frozenMul(e); // frozen solid: brittle (sim/elements.ts)
    if (by >= 0 && this.mut(Mut.Brittle)) dmg *= MUT_BRITTLE;
    // the roster lane (ROSTER.md): a Redshell only melee breaks (flag 16); anything else glances until it does
    if (e.redshell) { if (flags & 16) { e.redshell = 0; this.emit(Ev.Stagger, [e.id]); } else dmg *= REDSHELL_MUL; }
    if (e.type === EType.Stalker) e.revealT = secs(STALK_REVEAL); // a hit shows the Stalker
    { const hp = by >= 0 ? this.players.get(by) : undefined; if (hp) { const m = modsOf(hp); if (e.stunT > 0 && e.elUntil[2] > this.tick) dmg *= m.livewireMul; if (e.stripT > 0) dmg *= m.rustMul; if (e.lensT > 0 && e.lensBy === by) dmg *= m.lensMul; } } // Live Wire, Rust Never Sleeps, the Lens
    if (e.hyper && !(flags & 8)) dmg *= 0.1;
    let shieldDmg = 0;
    if (e.shield > 0 && !bypassShield) {
      shieldDmg = Math.min(e.shield, dmg);
      e.shield -= shieldDmg;
      dmg -= shieldDmg;
      e.shieldRegenT = secs(3);
      flags |= 2;
      if (e.affix === Affix.Aegis && e.shield <= 0) e.aegisT = secs(AEGIS_DELAY_SECS); // Aegis: the refill clock starts
    }
    if (e.aegisT > 0) e.aegisT = secs(AEGIS_DELAY_SECS); // any hit holds the refill off
    if (dmg > 0) {
      const av = armorOf(e);
      const m = bypassArmor ? 1 : apMultiplier(ap, av);
      if (m < 1) flags |= 4;
      dmg *= m;
      e.hp -= dmg;
    }
    e.lastHitBy = by;
    if (e.type === EType.Sniper) e.fleeT = secs(1.6);
    if (e.type === EType.Engineer) startEngineerFlee(e, by >= 0 ? this.players.get(by) : undefined, this.rng);
    if (e.target < 0 && by >= 0 && this.players.has(by)) e.target = by;
    this.emit(Ev.EnemyHit, [e.id, Math.round(dmg + shieldDmg), flags, by, hx, hy, hz]);
    if (e.hp <= 0) { this.killEnemy(e, by); return true; }
    return false;
  }

  /** weight: a hit whose stagger force beats the robot's resistance makes it reel (stunned, 2x damage) for STAGGER_SECS */
  stagger(e: Enemy, force: number) {
    if (e.dead || isBossType(e.type) || force <= ENEMIES[e.type].staggerRes * (1 - e.magCut)) return; // magnetic: resistance cut (sim/elements.ts)
    e.staggerT = Math.max(e.staggerT, secs(STAGGER_SECS));
    e.stunT = Math.max(e.stunT, secs(STAGGER_SECS));
  }

  killEnemy(e: Enemy, by: number) {
    if (e.dead) return;
    e.dead = true;
    { const k = by >= 0 ? this.players.get(by) : undefined; if (k) { const m = modsOf(k), c = CLASSES[k.cls]; if (m.killVent > 0 && k.heat >= c.heatMax * m.killVentFloor) k.heat = Math.max(0, k.heat - c.heatMax * m.killVent);
      if (m.kindling > 0 && e.burnT > 0) this.arsenal.blastAt(e.x, e.y + 0.5, e.z, 2.5, m.kindling, AP.Medium, by, Elem.Fire); // Kindling: a burning death pops
      k.killsNoHit = Math.min(255, k.killsNoHit + 1);
      if (m.bell > 0 && k.phoenixUsed && k.killsNoHit >= m.bell) { k.phoenixUsed = 0; k.killsNoHit = 0; this.emit(Ev.Notice, [], `${(k.name || 'A COWBOY').toUpperCase()}'S FEATHER RE-ARMED · THE BELL`); } } }
    this.arsenal.onEnemyKilled(e);
    this.emit(Ev.EnemyDie, [e.id, e.type, e.x, e.y, e.z, by < 0 ? 255 : by]);
    this.nemesis.onDeath(e, this);
    // morale (ROSTER.md): the wave's last anchor down routs its chaff; a Captain's brood panics and runs
    if (e.anchor && !this.mut(Mut.Unbreakable) && !this.mut(Mut.Zealous) && !this.enemies.some((o) => o !== e && !o.dead && o.anchor && o.wave === e.wave)) {
      let n = 0;
      for (const o of this.enemies) if (!o.dead && o.type === EType.Swarmer && o.wave === e.wave && !o.big && !o.nemesis) { o.routT = secs(ROUT_SECS); n++; }
      if (n > 0) this.emit(Ev.Notice, [], 'THE BIG ONE IS DOWN. THE CHAFF BREAKS AND RUNS.');
    }
    if (e.big) for (const o of this.enemies) if (!o.dead && o.broodOf === e.id) o.routT = secs(ROUT_SECS);
    if (e.gold && by >= 0) goldKilled(this, by); // a golden ticket (CHASE.md)
    if (e.type === EType.Helicopter && e.ward >= 0) { const w = this.enemies.find((o) => o.id === e.ward && !o.dead); if (w) { w.shield = 0; w.maxShield = ENEMIES[w.type].shield; this.emit(Ev.Notice, [], 'THE HELICOPTER IS DOWN: ITS WARD IS BARE'); } } // the projected shield dies with it
    if (this.mut(Mut.Scrapheap) && !isBossType(e.type)) for (const o of this.enemies) if (!o.dead && o !== e && len2(o.x - e.x, o.z - e.z) < MUT_SCRAP_R + ENEMIES[o.type].radius) this.damageEnemy(o, MUT_SCRAP_DMG, AP.Medium, by, 0, o.x, o.y + 1, o.z, true); // SCRAPHEAP: the pop
    if (e.nemesis) this.offerDrafts(DraftKind.Uncommon, 0x5e11 + e.id); // the named outlaw's spoils (Godot: the mini-boss card)
    if (isBossType(e.type) && this.boss && bossDef(this.boss.kind).died(this, this.boss, e)) { this.emit(Ev.Boss, [e.id, BossBeat.Dead, 0, 0]); bossDefeated(this); }
  }

  prime(e: Enemy, p: PlayerState) {
    if (e.dead) return;
    const fresh = e.primedBy < 0;
    e.primedBy = p.id;
    e.primeT = primeDuration(e.maxHp);
    // Sky-Stalker: inside her chain window, primes the Valkyrie lays from the air stick around longer
    if (p.cls === ClassId.Valkyrie && p.chainT > 0 && !p.grounded && e.primeT > 0) { const sp = modsOf(p).skyPrimeSecs; e.primeT = sp > 0 ? secs(sp) : e.primeT * SKY_PRIME_MUL; } // Cold Front
    if (fresh && e.primeStunCd === 0) { e.stunT = Math.max(e.stunT, secs(0.75)); e.primeStunCd = secs(4); }
    this.emit(Ev.Prime, [e.id, p.id]);
  }

  /** The spine of the combat: a primed robot, detonated by a class verb. */
  detonate(e: Enemy, p: PlayerState, direct: number): boolean {
    if (e.shield > 0) return false; // shields must drop first: the shield-breaker → detonator split
    const cross = e.primedBy >= 0 && e.primedBy !== p.id;
    const m = modsOf(p);
    const burst = clamp(0.35 * e.maxHp, 50, 400) * (cross ? 1.5 : 1);
    // the chain cards: Opening Act on the first link, Momentum from the Nth, Overdrive per link, Marshal's Eye on a Hot Shot
    let linkMul = (p.chainN === 0 ? m.chainOpenMul : 1) * (m.chainLinkFrom > 0 && p.chainN + 1 >= m.chainLinkFrom ? m.chainLinkMul : 1) * (1 + m.chainPerLink * p.chainN);
    if (p.cls === ClassId.Gunslinger && p.chainT > 0) linkMul *= m.hotshotDetMul;
    const total = (direct * (cross ? 3 : 2) + burst) * linkMul * m.dmgMul * (isConfused(e, this.tick) ? CONFUSION_MUL : 1); // two elements on it: confused
    // the conduits (sim/cards.ts): what the robot was under when it went
    const wasBurning = e.burnT > 0, wasIced = e.iceN > 0 || e.frozenT > 0, wasShocked = e.stunT > 0 && e.elUntil[2] > this.tick, wasStripped = e.stripT > 0, wasMag = e.magT > 0;
    e.primedBy = -1;
    this.detonations++;
    if (e.hyper) { e.hyper = 0; e.stunT = secs(3); this.emit(Ev.Stagger, [e.id]); }
    const c = CLASSES[p.cls];
    const ex = e.x, ey = e.y + ENEMIES[e.type].bodyY, ez = e.z;
    let radius = (p.cls === ClassId.Valkyrie ? 3.5 : p.cls === ClassId.Phantom ? 5 : 4) * (1 + m.detRadiusHeat * (p.heat / c.heatMax)); // Furnace Detonator
    this.damageEnemy(e, total, 4, p.id, 8, ex, ey, ez, true);
    this.shedPanel(e); // panel_shed.gd: a detonation on a heavy knocks a plate off
    if (m.conduitFire && wasBurning) p.heat = Math.max(0, p.heat - c.heatMax * 0.15);
    if (m.conduitIce && wasIced) p.shield = Math.min(Math.max(0, c.shieldMax + m.shieldAdd), p.shield + 10);
    if (m.conduitShock && wasShocked) p.dodgeCd = Math.max(0, p.dodgeCd - secs(1.5));
    if (m.conduitAcid && wasStripped) p.acidBoostT = secs(6);
    if (m.conduitMag && wasMag) for (const o of this.enemies) if (!o.dead && len2(o.x - ex, o.z - ez) < 8) { o.magCut = Math.max(o.magCut, 0.5); o.magT = Math.max(o.magT, secs(6)); }
    // chain window + loop extenders
    // the window: the class's, plus the hand's (Long Fuse, Hot Streak, Stormcloud, All In halves it); Relay Runner pays
    // a teammate's prime more than your own
    p.chainT = Math.round((c.chainTicks + m.chainAdd + (p.cls === ClassId.Valkyrie ? m.skyAdd : 0) + (cross ? m.chainRelayAdd : m.chainSoloAdd)) * m.chainMul);
    p.chainN = Math.min(255, p.chainN + 1);
    p.heat = Math.max(0, p.heat - c.heatMax * (cross ? m.chainRelayRefund : m.chainSoloRefund));
    if (p.cls === ClassId.Valkyrie && !p.grounded) p.heat = Math.max(0, p.heat - c.heatMax * (m.skyRefund > 0 ? m.skyRefund : 0.25)); // Thunderhead
    if (p.cls === ClassId.Gunslinger && m.hotshotRefund > 0) p.heat = Math.max(0, p.heat - c.heatMax * m.hotshotRefund); // Quickdraw
    if (p.cls === ClassId.Phantom) this.openCloak(p, m, Math.round(DETONATE_CLOAK + m.cloakAdd));
    this.emit(Ev.Detonate, [e.id, p.id, p.cls, ex, ey, ez, radius, p.chainN, cross ? 1 : 0]);
    this.emit(Ev.Chain, [p.id, p.chainN]);
    // splash, flavour, chaff clear, knockback
    const targets = this.enemies.filter((o) => !o.dead && o !== e && len2(o.x - ex, o.z - ez) < radius + ENEMIES[o.type].radius && Math.abs(o.y - e.y) < 4);
    if (p.cls === ClassId.Phantom) {
      // Chain Fracture: arcs to the three nearest, executes the weak
      targets.sort((a, b) => len2(a.x - ex, a.z - ez) - len2(b.x - ex, b.z - ez));
      for (const o of targets.slice(0, 3)) {
        if (o.hp <= o.maxHp * 0.1) { this.killEnemy(o, p.id); continue; }
        this.damageEnemy(o, total * 0.5, 4, p.id, 8, o.x, o.y + 1, o.z, true);
      }
    }
    for (const o of targets) {
      if (o.dead) continue;
      if (o.maxHp < 60) { this.damageEnemy(o, o.maxHp * 2, 4, p.id, 8, o.x, o.y + 0.6, o.z, true); continue; }
      if (p.cls !== ClassId.Phantom) this.damageEnemy(o, total * 0.35, 4, p.id, 8, o.x, o.y + 1, o.z, true);
      if (p.cls === ClassId.Gunslinger) o.vulnT = secs(3);
      if (p.cls === ClassId.Valkyrie) { o.burnT = secs(3); o.burnOwner = p.id; }
      if (p.cls === ClassId.Juggernaut) { o.stunT = Math.max(o.stunT, secs(1.5)); o.stripT = secs(6); }
      const kx = o.x - ex, kz = o.z - ez, kl = len2(kx, kz) || 1;
      const push = 9 / ENEMIES[o.type].weight;
      o.kx += (kx / kl) * push; o.kz += (kz / kl) * push;
    }
    if (!e.dead) {
      if (p.cls === ClassId.Gunslinger) e.vulnT = secs(3);
      if (p.cls === ClassId.Valkyrie) { e.burnT = secs(3); e.burnOwner = p.id; }
      if (p.cls === ClassId.Juggernaut) { e.stunT = Math.max(e.stunT, secs(1.5)); e.stripT = secs(6); }
    }
    return true;
  }

  /** The Phantom's blink strike (hold R3): to the nearest robot in a cone ahead, 1 m past it facing back; it's primed,
   *  stunned and everything near is shoved. Costs BLINK_HEAT, free from a hover. The client turns the camera on Ev.Blink. */
  private resolveBlink(p: PlayerState, inp: InputFrame) {
    const hover = p.move === Move.Hover ? 1 : 0;
    const range = BLINK_RANGE * (hover ? BLINK_HOVER_MUL : 1), heat = BLINK_HEAT * (1 - hover);
    if (p.heat + heat >= CLASSES[p.cls].heatMax) return;
    const fx = -Math.sin(inp.myaw), fz = -Math.cos(inp.myaw);
    const view = clamp(inp.viewTick || this.tick, this.tick - LAG_COMP_MAX_TICKS, this.tick);
    const hp = { x: 0, y: 0, z: 0, yaw: 0 };
    let best: Enemy | null = null, bestD = 1e9;
    for (const e of this.enemies) {
      if (e.dead || isBossType(e.type)) continue;
      historyAt(e, view, this.tick, hp);
      const dx = hp.x - p.x, dz = hp.z - p.z, d = len2(dx, dz);
      if (d > range + ENEMIES[e.type].radius || Math.abs(hp.y - p.y) > 3) continue;
      if (d > 0.3 && (dx / d) * fx + (dz / d) * fz < BLINK_CONE) continue;
      if (d < bestD) { bestD = d; best = e; }
    }
    if (!best) return; // a whiff costs nothing
    const e = best;
    p.heat = Math.min(CLASSES[p.cls].heatMax, p.heat + heat);
    const ax = e.x - p.x, az = e.z - p.z, al = len2(ax, az) || 1, ux = ax / al, uz = az / al;
    const fromX = p.x, fromZ = p.z;
    const q = { x: e.x + ux * 1.0, y: e.y, z: e.z + uz * 1.0 };
    const g = this.world.support(q.x, q.z, e.y + 2);
    q.y = g === -1e9 ? e.y : g;
    this.world.pushOut(q, PLAYER_RADIUS, PLAYER_HEIGHT);
    p.x = q.x; p.z = q.z; p.y = q.y; p.vx = p.vz = 0; p.vy = 0;
    p.grounded = 1; p.move = Move.Ground; p.hoverMode = 0; p.stallT = 0;
    p.iframes = Math.max(p.iframes, BLINK_IFRAMES);
    p.meleeCd = Math.max(p.meleeCd, BLINK_CD); p.meleeT = secs(0.25); p.blade = 1;
    // the strike: a backstab from any angle (primes), the stun, the shove of everything near
    const killed = this.damageEnemy(e, BLINK_DMG, 1, p.id, 0, e.x, e.y + 1, e.z);
    if (!killed) { this.prime(e, p); e.stunT = Math.max(e.stunT, BLINK_STUN); this.stagger(e, 180); }
    for (const o of this.enemies) {
      if (o.dead) continue;
      const kx = o.x - p.x, kz = o.z - p.z, kl = len2(kx, kz);
      if (kl > BLINK_KNOCK_R + ENEMIES[o.type].radius || kl < 1e-3) continue;
      const f = BLINK_KNOCK * (1 - kl / (BLINK_KNOCK_R + 1)) / ENEMIES[o.type].weight;
      o.kx += (kx / kl) * f; o.kz += (kz / kl) * f;
    }
    this.emit(Ev.Melee, [p.id, 0]);
    this.emit(Ev.Blink, [p.id, e.id, fromX, fromZ, p.x, p.y, p.z]);
  }

  /** The warp's sweep: robots on the line it crossed are clipped aside and can be backstabbed from any angle for a while. */
  private warpSweep(p: PlayerState, x0: number, z0: number) {
    const dx = p.x - x0, dz = p.z - z0, l = len2(dx, dz);
    if (l < 0.5) return;
    const ux = dx / l, uz = dz / l;
    for (const e of this.enemies) {
      if (e.dead || isBossType(e.type)) continue;
      const ex = e.x - x0, ez = e.z - z0;
      const t = clamp(ex * ux + ez * uz, 0, l);
      const px = x0 + ux * t, pz = z0 + uz * t;
      const d = len2(e.x - px, e.z - pz);
      if (d > WARP_CLIP_R + ENEMIES[e.type].radius * 0.5 || Math.abs(e.y - p.y) > 3) continue;
      const side = (e.x - px) * -uz + (e.z - pz) * ux >= 0 ? 1 : -1;
      const f = WARP_CLIP_FORCE / ENEMIES[e.type].weight;
      e.kx += -uz * side * f; e.kz += ux * side * f;
      e.graceBy = p.id; e.graceT = WARP_GRACE;
    }
  }

  /** The Juggernaut's charged hammer: a wide heavy cone that DETONATES primed robots; damage, cone, stagger and shove grow with the charge. */
  private resolveHammer(p: PlayerState, inp: InputFrame, charge: number) {
    const c = CLASSES[p.cls];
    if (p.heat + HAMMER_HEAT < c.heatMax) p.heat += HAMMER_HEAT;
    p.meleeCd = Math.max(p.meleeCd, HAMMER_CD);
    const fx = -Math.sin(p.yaw), fz = -Math.cos(p.yaw);
    const cone = Math.cos(((HAMMER_CONE + 10 * charge) / 2) * Math.PI / 180);
    const view = clamp(inp.viewTick || this.tick, this.tick - LAG_COMP_MAX_TICKS, this.tick);
    const hp = { x: 0, y: 0, z: 0, yaw: 0 };
    const dmg = HAMMER_DMG * (1 + charge);
    for (const e of this.enemies) {
      if (e.dead) continue;
      historyAt(e, view, this.tick, hp);
      const dx = hp.x - p.x, dz = hp.z - p.z, dl = len2(dx, dz) || 1;
      if (dl - ENEMIES[e.type].radius > HAMMER_RANGE || Math.abs(hp.y - p.y) > 2.5) continue;
      if ((dx / dl) * fx + (dz / dl) * fz < cone && dl > 0.6) continue;
      if (e.primedBy >= 0 && this.detonate(e, p, dmg)) continue;
      const hb = modsOf(p).jugHammerBank; if (hb > 0) this.arsenal.onShieldDamage(p, dmg * hb); // Heavy Hands
      const killed = this.damageEnemy(e, dmg * modsOf(p).dmgMul * modsOf(p).meleeMul * (e.iceN > 0 || e.frozenT > 0 ? modsOf(p).shatterMul : 1), 2, p.id, 16, hp.x, hp.y + 1, hp.z);
      if (killed) continue;
      this.stagger(e, STAGGER_MELEE * (1 + charge) * modsOf(p).staggerMul);
      const w = ENEMIES[e.type].weight;
      e.kx += (dx / dl) * 14 * (1 + charge) / w; e.kz += (dz / dl) * 14 * (1 + charge) / w;
    }
    this.emit(Ev.Melee, [p.id, 2]);
  }

  /** The Valkyrie's ground pound landed: a slam that burns the whole shield she had, sized by the height fallen,
   *  detonating primed robots in it. The burnt shield banks like any shield damage. */
  private resolvePound(p: PlayerState) {
    const c = CLASSES[p.cls];
    const h = Math.max(0, p.poundY - p.y), hr = clamp(h / POUND_H, 0, 1);
    const radius = POUND_R0 + (POUND_R1 - POUND_R0) * hr;
    const sr = clamp(p.shield / c.shieldMax, 0, 1);
    const dmg = POUND_DMG * (1 + hr) * sr;
    const burnt = p.shield;
    p.shield = 0; p.shieldDelay = Math.max(p.shieldDelay, shieldDelayFor(p.cls));
    this.arsenal.onShieldDamage(p, burnt);
    p.heat = Math.min(c.heatMax, p.heat + POUND_HEAT * sr);
    for (const e of this.enemies) {
      if (e.dead) continue;
      const d = len2(e.x - p.x, e.z - p.z) - ENEMIES[e.type].radius;
      if (d > radius || Math.abs(e.y - p.y) > 3) continue;
      if (e.primedBy >= 0 && this.detonate(e, p, dmg)) continue;
      const killed = this.damageEnemy(e, dmg, 2, p.id, 0, e.x, e.y + 1, e.z);
      if (killed) continue;
      this.stagger(e, 200 * sr);
      const kx = e.x - p.x, kz = e.z - p.z, kl = len2(kx, kz) || 1;
      e.kx += (kx / kl) * 10 * sr / ENEMIES[e.type].weight; e.kz += (kz / kl) * 10 * sr / ENEMIES[e.type].weight;
    }
    this.emit(Ev.Pound, [p.id, p.x, p.y, p.z, radius]);
  }

  /** a ground slam around a cowboy (cards): staggers, shoves, and (dmg > 0) hurts robots within r */
  private slamAround(p: PlayerState, r: number, dmg: number, staggerForce: number, label: string) {
    for (const e of this.enemies) {
      if (e.dead) continue;
      const kx = e.x - p.x, kz = e.z - p.z, kl = len2(kx, kz);
      if (kl > r + ENEMIES[e.type].radius || Math.abs(e.y - p.y) > 3) continue;
      if (dmg > 0 && this.damageEnemy(e, dmg, 2, p.id, 0, e.x, e.y + 1, e.z)) continue;
      this.stagger(e, staggerForce);
      const f = 8 / ENEMIES[e.type].weight; e.kx += (kx / (kl || 1)) * f; e.kz += (kz / (kl || 1)) * f;
    }
    this.emit(Ev.Explosion, [p.x, p.y + 0.3, p.z, r, 4]);
    void label;
  }

  /** heal up to the hull cap the hand allows */
  heal(p: PlayerState, amount: number) { if (amount > 0 && p.phase === Phase.Alive) p.hp = Math.min(hullMaxOf(p, modsOf(p)), p.hp + amount); }

  /** the hand's always-on damage multiplier for a cowboy's own hits: Hot Hand above its floor, the chain cards while a
   *  chain is open, Ride Alone as the last one standing (the per-hit gun mods are in gunHitMul) */
  dealMul(p: PlayerState, m: Mods) {
    const c = CLASSES[p.cls];
    let k = m.dmgMul;
    if (m.hotHand > 0) { const f = p.heat / c.heatMax; if (f >= m.hotHandFloor) k *= 1 + m.hotHand * Math.min(1, (f - m.hotHandFloor) / Math.max(0.05, 1 - m.hotHandFloor)); }
    if (p.chainT > 0) k *= m.chainDmgMul;
    if (m.rideAlone && this.players.size > 1 && [...this.players.values()].every((o) => o === p || o.phase !== Phase.Alive)) k *= 1.5;
    return k;
  }
  /** the weapon-mod cards on one gun hit: Overcharge every Nth round, Long Barrel by range, Hollow Points / Sabot by armour */
  gunHitMul(p: PlayerState, m: Mods, dist: number, av: number) {
    let k = m.firearmMul;
    if (m.overcharge > 0 && p.shotN % m.overcharge === 0) k *= 2;
    if (m.longBarrel) k *= dist > 25 ? 1.25 : 0.85;
    if (m.hollow) k *= av > 0 ? 0.7 : 1.3;
    if (m.sabot) k *= av > 0 ? 1.3 : 0.7;
    return k;
  }
  /** the Phantom's cloak opens: Second Skin pays shield, Blood Price takes hull */
  private openCloak(p: PlayerState, m: Mods, ticks: number) {
    if (p.cloakT <= 0) { p.echoUsed = 0; if (m.cloakShield > 0) p.shield += m.cloakShield; if (m.cloakPrice > 0) p.hp = Math.max(1, p.hp - m.cloakPrice); }
    p.cloakT = Math.max(p.cloakT, ticks);
  }

  // ------------------------------------------------------------------ wildcard cards (sim/cards.ts)
  /** put a draft on every living cowboy's table, rolled from the run seed and `salt` (an island, a boss, an outlaw) */
  offerDrafts(kind: DraftKind, salt: number) {
    for (const p of this.players.values()) {
      if (p.phase === Phase.Dead) continue;
      if (offerDraft(p, rollDraft(this.seed, salt, p, kind), kind)) this.emit(Ev.Card, [p.id, 1, kind]);
    }
  }
  /** the hexed strongbox (Godot hexed_strongbox.gd): placed by sim/run.ts on some islands; hold Interact in reach for 0.8 s to open
   *  YOUR copy of it: a forced draft of three curses, then a Rare */
  placeStrongbox(x: number, z: number) {
    const y = this.world.support(x, z, 60);
    this.strongbox = { x, y, z, openedMask: 0, holdT: [] };
    this.emit(Ev.Strongbox, [x, y, z, 1, 255]);
  }
  private stepStrongbox() {
    const b = this.strongbox;
    if (!b) return;
    for (const p of this.players.values()) {
      if (p.phase !== Phase.Alive || (b.openedMask >> p.slot) & 1 || p.offerKind !== DraftKind.None) continue;
      const near = len2(p.x - b.x, p.z - b.z) < STRONGBOX_R && Math.abs(p.y - b.y) < 2.5;
      const holding = near && ((this.inputs.get(p.id)?.buttons ?? 0) & Btn.Interact) !== 0;
      b.holdT[p.id] = holding ? (b.holdT[p.id] ?? 0) + 1 : 0;
      if (b.holdT[p.id] === 1) this.emit(Ev.Notice, [], 'THE HEXED STRONGBOX · HOLD TO OPEN: THREE CURSES, THEN A RARE');
      if (b.holdT[p.id] < STRONGBOX_HOLD) continue;
      b.openedMask |= 1 << p.slot;
      const cards = rollDraft(this.seed, 0x4e3d + (this.run.village * 3 + this.run.island), p, DraftKind.Cursed);
      if (cards.length && offerDraft(p, cards, DraftKind.Cursed)) { this.hexRare.add(p.id); this.emit(Ev.Card, [p.id, 1, DraftKind.Cursed]); }
      this.emit(Ev.Strongbox, [b.x, b.y, b.z, 0, p.id]);
    }
  }
  /** visible damage (playtest 3, Nick): the part a round landed on takes it; past PART_POP_FRAC of the hull that part comes off
   *  while the robot lives (Ev.PartPop; the client hides it, smokes it, throws the chips). A leg gone: it limps. The right
   *  arm gone on a Shooter or Sniper: disarmed (the gun router). The top gone: a beat of stun. Bosses and the Metal Brute keep whole. */
  wearPart(e: Enemy, dmg: number, hx: number, hy: number, hz: number) {
    if (e.dead || isBossType(e.type) || e.type === EType.MetalBrute || e.type === EType.Turret || dmg <= 0) return;
    const d = ENEMIES[e.type];
    const h = (hy - e.y) / Math.max(0.5, d.headY);
    const rx = Math.cos(e.yaw), rz = -Math.sin(e.yaw); // the robot's right
    const side = (hx - e.x) * rx + (hz - e.z) * rz;
    const part: Part = h > 0.85 ? Part.Top : h < 0.4 ? Part.Legs : side > d.radius * 0.3 ? Part.RightArm : side < -d.radius * 0.3 ? Part.LeftArm : Part.Legs;
    if ((e.parts >> part) & 1) return;
    e.partDmg[part] += dmg;
    if (e.partDmg[part] < e.maxHp * PART_POP_FRAC) return;
    e.parts |= 1 << part;
    this.emit(Ev.PartPop, [e.id, part, hx, hy, hz]);
    if (part === Part.Legs) e.limp = 1;
    else if (part === Part.RightArm && e.gunHp > 0) { e.gunHp = 0; this.emit(Ev.Notice, [], e.type === EType.Sniper ? 'RIFLE ARM SHOT OFF: THE SNIPER IS DONE SHOOTING' : 'GUN ARM SHOT OFF: IT CAN ONLY CLAW NOW'); }
    else if (part === Part.Top) e.stunT = Math.max(e.stunT, secs(0.5));
  }
  /** panel_shed.gd: a Brute (or Metal Brute) loses a plate to a detonation on it or a blast of SHED_BLAST_DMG+ touching it:
   *  a tier of armour off and the back weak arc wider, up to SHED_MAX, one a second */
  shedPanel(e: Enemy) {
    if (e.dead || e.type !== EType.Brute || e.shed >= SHED_MAX || e.shedCd > 0) return; // the Metal Brute's AV4 gate is its own puzzle (CLAUDE.md gotcha 1)
    e.shed++; e.shedCd = secs(SHED_CD_SECS);
    e.armor = Math.max(0, e.armor - 1);
    this.emit(Ev.Stagger, [e.id]);
    this.emit(Ev.Notice, [], e.shed >= SHED_MAX ? 'ANOTHER PLATE OFF: ITS CORE SHOWS' : 'A PLATE OFF THE BRUTE: GET ROUND IT');
  }
  /** elemental barrels by the buildings: clusters of one to three (the cluster's size is the element's level) */
  placeBarrels(seedSalt: number) {
    const rng = new Rng(hash32(this.seed, seedSalt, 0xba77));
    const builds = this.world.boxes.filter((b) => b.kind === 'building');
    let id = 1;
    for (const b of builds) {
      if (!rng.chance(0.35)) continue;
      const n = rng.int(1, 3), el = ELEMS[rng.int(0, 4)];
      const side = rng.chance(0.5) ? 1 : -1, ax = b.x + side * (b.hx + 1.2), az = b.z + rng.range(-b.hz * 0.6, b.hz * 0.6);
      for (let i = 0; i < n; i++) {
        const x = ax + (i - (n - 1) / 2) * 1.1 * 0.3, z = az + (i - (n - 1) / 2) * 1.1;
        const y = this.world.support(x, z, b.y1 + 2);
        if (y === NO_GROUND) continue;
        const bar = { id: id++, el, x, y, z, hp: BARREL_HP, level: n, fuse: 0, by: -1, dead: false };
        this.barrels.push(bar);
        this.emit(Ev.Barrel, [bar.id, el + 1, x, y, z, 1, n]);
      }
    }
  }
  hitBarrel(b: { hp: number; fuse: number; by: number; dead: boolean }, dmg: number, by: number) {
    if (b.dead || b.fuse > 0) return;
    b.hp -= dmg;
    if (b.hp <= 0) { b.fuse = 1; b.by = by; }
  }
  private stepBarrels() {
    for (const b of this.barrels) {
      if (b.dead || b.fuse <= 0) continue;
      if (--b.fuse > 0) continue;
      b.dead = true;
      this.emit(Ev.Barrel, [b.id, b.el + 1, b.x, b.y, b.z, 0, b.level]);
      this.arsenal.blastAt(b.x, b.y + 0.5, b.z, BARREL_R, BARREL_DMG, AP.Medium, b.by, b.el, b.level);
      this.elemBlast(b.el, b.by, b.x, b.y + 0.5, b.z, 0.6 * b.level);
      // the blast chains the barrels it touches (next tick each) and sheds a panel off a heavy it touches
      for (const o of this.barrels) if (!o.dead && o !== b && o.fuse === 0 && len2(o.x - b.x, o.z - b.z) < BARREL_R) { o.fuse = BARREL_FUSE_TICKS; o.by = b.by; }
      for (const e of this.enemies) if (!e.dead && len2(e.x - b.x, e.z - b.z) < BARREL_R + ENEMIES[e.type].radius) this.shedPanel(e);
    }
  }
  placeCache(x: number, z: number) {
    const y = this.world.support(x, z, 60);
    this.cache = { x, y, z, mask: 0, holdT: [] };
    this.emit(Ev.Strongbox, [x, y, z, 3, 255]);
  }
  private stepCache() {
    const b = this.cache;
    if (!b) return;
    for (const p of this.players.values()) {
      if (p.phase !== Phase.Alive || (b.mask >> p.slot) & 1) continue;
      const near = len2(p.x - b.x, p.z - b.z) < STRONGBOX_R && Math.abs(p.y - b.y) < 2.5;
      const holding = near && ((this.inputs.get(p.id)?.buttons ?? 0) & Btn.Interact) !== 0;
      b.holdT[p.id] = holding ? (b.holdT[p.id] ?? 0) + 1 : 0;
      if (b.holdT[p.id] === 1) this.emit(Ev.Notice, [], 'A MUNITIONS CACHE · HOLD TO LEVEL YOUR GRENADE ELEMENT');
      if (b.holdT[p.id] < CACHE_HOLD) continue;
      b.mask |= 1 << p.slot;
      const lvl = Math.max(1, p.nadeLevel);
      if (lvl < 3) { p.nadeLevel = lvl + 1; this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()}'S GRENADES: ELEMENT L${p.nadeLevel}`); }
      else { p.nades = nadeCap(p); this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()}'S BELT REFILLED (L3 ALREADY)`); }
      this.emit(Ev.Strongbox, [b.x, b.y, b.z, 2, p.id]);
    }
  }
  /** C2S.PickCard: slot 0..2 of the offer, or 255 to pass */
  pickCard(pid: number, slot: number, wslot = 0) {
    const p = this.players.get(pid);
    if (!p) return;
    const got = pickFromOffer(p, slot === 255 ? -1 : slot, wslot);
    if (got < 0) return;
    if (this.hexRare.has(pid)) { this.hexRare.delete(pid); const cards = rollDraft(this.seed, 0x4e3d + (this.run.village * 3 + this.run.island), p, DraftKind.Rare); if (offerDraft(p, cards, DraftKind.Rare)) this.emit(Ev.Card, [p.id, 1, DraftKind.Rare]); } // the strongbox's payoff
    const wasKind = p.offerKind;
    const tiers = Object.keys(SET_BONUS).map((f) => setTier(p, f));
    if (got > 0) {
      this.emit(Ev.Card, [p.id, 2, got]);
      Object.keys(SET_BONUS).forEach((f, i) => { const t = setTier(p, f); if (t > tiers[i]) this.emit(Ev.Notice, [], `${f.replace(/_/g, ' ').toUpperCase()} SET ${t === 2 ? 'II' : 'I'} · ${SET_BONUS[f][t - 1].name.toUpperCase()}`); });
      this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()} TOOK ${CARDS[got].name.toUpperCase()}`);
      // a card that raises the caps raises the bars with it, so the pick is felt at once
      const m = modsOf(p);
      p.hp = Math.min(hullMaxOf(p, m), p.hp + Math.max(0, CARDS[got].fx.hpAdd ?? 0));
      p.shield = Math.min(Math.max(0, CLASSES[p.cls].shieldMax + m.shieldAdd), p.shield + Math.max(0, CARDS[got].fx.shieldAdd ?? 0));
    } else this.emit(Ev.Card, [p.id, 3, 0]);
  }

  private resolveMelee(p: PlayerState, inp: InputFrame) {
    const c = CLASSES[p.cls];
    const range = p.cls === ClassId.Juggernaut ? 3.3 : 2.7;
    const fx = -Math.sin(p.yaw), fz = -Math.cos(p.yaw);
    const view = clamp(inp.viewTick || this.tick, this.tick - LAG_COMP_MAX_TICKS, this.tick);
    let hits = 0, primed = 0;
    const hp = { x: 0, y: 0, z: 0, yaw: 0 };
    for (const e of this.enemies) {
      if (e.dead) continue;
      historyAt(e, view, this.tick, hp);
      const dx = hp.x - p.x, dz = hp.z - p.z;
      const d = len2(dx, dz) - ENEMIES[e.type].radius;
      if (d > range || Math.abs(hp.y - p.y) > 2.5) continue;
      const dl = len2(dx, dz) || 1;
      if ((dx / dl) * fx + (dz / dl) * fz < 0.45 && d > 0.4) continue; // ~60° cone
      hits++;
      const efx = -Math.sin(e.yaw), efz = -Math.cos(e.yaw);
      const behind = efx * (dx / dl) + efz * (dz / dl) > 0.2; // robot faces away from you
      let dmg = 9, prime = false, push = 6;
      switch (p.cls) {
        case ClassId.Gunslinger: prime = true; dmg = 8; push = 8; break; // pistol whip
        case ClassId.Phantom: dmg = PHANTOM_JAB; if (behind || p.cloakT > 0 || (e.graceBy === p.id && e.graceT > 0)) prime = true; break; // backstab (or the warp's grace)
        case ClassId.Juggernaut: prime = true; dmg = 12; push = 14; break; // shield bash
        case ClassId.Valkyrie: dmg = 10; break;
      }
      if (p.stimT > 0) dmg = Math.round(dmg * ADREN_MELEE); // whiskey courage (sim/whiskey.ts)
      const m = modsOf(p);
      dmg = dmg * m.meleeMul * this.dealMul(p, m) * (e.iceN > 0 || e.frozenT > 0 ? m.shatterMul : 1); // Shatter
      const hp0 = e.hp + e.shield;
      const killed = this.damageEnemy(e, dmg, 1, p.id, 0, hp.x, hp.y + 1, hp.z);
      const dealt = Math.max(0, hp0 - (killed ? 0 : e.hp + e.shield));
      if (m.lifesteal > 0) this.heal(p, dealt * m.lifesteal); // Rattler Fang, Bloodletter
      if (prime && p.cls === ClassId.Phantom && m.cloakHeal > 0) this.heal(p, m.cloakHeal); // Blood Price
      if (!killed) this.elemHit(p, e);
      if (!killed && prime) { this.prime(e, p); primed++; }
      const w = ENEMIES[e.type].weight;
      e.kx += (dx / dl) * push / w; e.kz += (dz / dl) * push / w;
      if (!killed) this.stagger(e, STAGGER_MELEE * m.staggerMul);
    }
    // a barrel in reach: the swing kicks it off (elemental_barrel.gd melee_launch, simplified to a short fuse)
    for (const b of this.barrels) if (!b.dead && b.fuse === 0 && len2(b.x - p.x, b.z - p.z) < range + BARREL_RADIUS && Math.abs(b.y - p.y) < 2) { const dxb = b.x - p.x, dzb = b.z - p.z, l = len2(dxb, dzb) || 1; if ((dxb / l) * fx + (dzb / l) * fz > 0.3) { b.fuse = secs(0.4); b.by = p.id; } }
    void c;
    this.emit(Ev.Melee, [p.id, hits, primed, p.combo]);
  }

  private resolveSpecial(p: PlayerState) {
    const set = this.specialHits.get(p.id);
    if (!set) return;
    for (const e of this.enemies) {
      if (e.dead || set.has(e.id)) continue;
      const d = ENEMIES[e.type];
      const reach = d.radius + (p.cls === ClassId.Juggernaut ? 1.3 : 1.0);
      if (len2(e.x - p.x, e.z - p.z) > reach || p.y + 1 < e.y - 0.5 || p.y > e.y + d.headY + 0.5) continue;
      set.add(e.id);
      if (p.cls === ClassId.Valkyrie) {
        // the dash: pass through to prime; it carries the held gun's element (Godot valkyrie.tres: an Acid L3 gun lays
        // Acid L3 on the pass); with none installed it is the web's fire dash
        this.prime(e, p);
        const el = gunElem(p, p.wSlot);
        if (el !== Elem.None) applyProc(this, e, el, gunLevel(p, p.wSlot), p.id, 10);
        else { e.burnT = Math.max(e.burnT, secs(2)); e.burnOwner = p.id; }
      } else if (p.cls === ClassId.Juggernaut) {
        // charge: detonates what's primed, bowls over the rest
        const fx = -Math.sin(p.yaw), fz = -Math.cos(p.yaw);
        const f = this.chargeF.get(p.id) ?? 0;
        if (e.primedBy >= 0 && this.detonate(e, p, CHARGE_DET_BASE + (CHARGE_DET_FULL - CHARGE_DET_BASE) * f)) continue;
        this.damageEnemy(e, CHARGE_DMG_BASE + (CHARGE_DMG_FULL - CHARGE_DMG_BASE) * f, 2, p.id, 0, e.x, e.y + 1, e.z);
        e.kx += fx * (12 + 16 * f) / d.weight; e.kz += fz * (12 + 16 * f) / d.weight;
        this.stagger(e, STAGGER_CHARGE * (0.5 + 0.5 * f) * modsOf(p).staggerMul);
        e.stunT = Math.max(e.stunT, secs(0.4));
      }
    }
  }

  // ------------------------------------------------------------------ players hurt, down, revive
  /** Returns true if the hit landed (not rolled through, not paused or down). */
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx = 0, kz = 0, bypassShield = false, src: HitSrc | null = null) {
    if (!isTargetable(p) || p.iframes > 0 || p.phase !== Phase.Alive) return false;
    if (src?.nemesis) dmg *= NEMESIS_DMG_MUL;
    if (src && this.mut(Mut.Bloodthirsty)) dmg *= MUT_BLOOD; // a robot's hit, under BLOODTHIRSTY
    // the hand (sim/cards.ts): Glass Cannon and friends, Boiler Plate in the red, Ride Alone while a teammate is down
    const m = modsOf(p);
    dmg *= m.takenMul;
    if (p.heat >= CLASSES[p.cls].heatMax * 0.8) dmg *= m.edgeTakenMul;
    if (m.rideAlone && [...this.players.values()].some((o) => o !== p && o.phase === Phase.Down)) dmg *= 1.25;
    // Metal Skin: direct hits glance off (hazards that skip the shield still bite)
    if (p.puAct === PU.MetalSkin && !bypassShield) dmg *= METAL_TAKE;
    let rest = dmg;
    let broke = 0;
    // the overshield soaks first and never comes back
    if (p.over > 0 && !bypassShield) { const o = Math.min(p.over, rest); p.over -= o; rest -= o; this.arsenal.onShieldDamage(p, o); } // the overshield banks too (Godot shield_impacted)
    if (p.shield > 0 && !bypassShield) {
      const s = Math.min(p.shield, rest);
      p.shield -= s; rest -= s;
      this.arsenal.onShieldDamage(p, s);
      if (p.shield <= 0) broke = 1;
    }
    if (!bypassShield) p.shieldDelay = Math.max(p.shieldDelay, shieldDelayFor(p.cls));
    if (p.acidT > 0) rest *= ACID_VULN_MUL; // acid-softened (sim/elements.ts)
    if (dmg > 0) p.killsNoHit = 0; // Bartender's Bell counts from the last hit taken
    p.hp -= rest;
    p.vx += kx; p.vz += kz;
    // weight (defs.ts KNOCK_*): every hit lands with a beat of hit stun; a hard shove breaks flight, a harder one floors
    // you, and the client ragdolls the body past KNOCK_RAGDOLL. The shove itself coasts out under KNOCK_FRICTION.
    const k = len2(kx, kz);
    if (dmg > 0) p.hitStunT = Math.max(p.hitStunT, Math.min(HIT_STUN_MAX, HIT_STUN + Math.round(HIT_STUN_PER_FRAC * Math.min(1, dmg / HULL_MAX)))); // a beat, longer for a hard hit
    if (k >= KNOCK_FLIGHT && (p.move === Move.Flight || p.move === Move.Hover)) { p.move = Move.Air; p.hoverMode = 0; p.stallT = 0; }
    if (k >= KNOCK_DOWN && p.knockCd === 0 && p.move !== Move.Special && p.knockT === 0) {
      p.knockT = KNOCKDOWN + GETUP; p.knockCd = KNOCK_CD;
      if (p.move === Move.Dodge) { p.dodgeT = 0; p.move = p.grounded ? Move.Ground : Move.Air; }
      p.sprint = 0; p.hoverMode = 0;
      this.emit(Ev.Knock, [p.id, kx, kz, k >= KNOCK_RAGDOLL ? KnockKind.Ragdoll : KnockKind.Down]);
    }
    const vl = len2(p.vx, p.vz);
    if (vl > KNOCK_MAX) { p.vx *= KNOCK_MAX / vl; p.vz *= KNOCK_MAX / vl; }
    if (p.cls === ClassId.Phantom && p.cloakT > 0) { if (m.cloakEcho > 0 && !p.echoUsed) p.echoUsed = 1; else p.cloakT = 0; } // any shield hit shimmers the cloak (Echo forgives one)
    this.emit(Ev.PlayerHurt, [p.id, Math.round(dmg), fx, fz, broke | flags]);
    if (p.hp <= 0 && ((m.horseshoe && !p.horseshoeUsed) || (m.phoenix && !p.phoenixUsed) || this.sharedWatch > 0)) {
      // Lucky Horseshoe (once an island) or the Phoenix Feather (once a run; Mortal Coil re-arms it at a price); the Posse
      // Repair Kit's shared feather catches anyone with nothing of their own left
      if (m.horseshoe && !p.horseshoeUsed) { p.horseshoeUsed = 1; p.hp = 1; this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()}'S LUCKY HORSESHOE HELD`); }
      else if (!(m.phoenix && !p.phoenixUsed)) { this.sharedWatch--; p.hp = 1; p.iframes = Math.max(p.iframes, secs(1.5)); this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()} RISES ON THE POSSE'S FEATHER (${this.sharedWatch} LEFT)`); }
      else { p.phoenixUsed = m.phoenixRearm ? 0 : 1; p.hpLost += m.phoenixPrice; p.hp = 1; p.iframes = Math.max(p.iframes, secs(1.5)); this.emit(Ev.Notice, [], `${(p.name || 'A COWBOY').toUpperCase()} RISES FROM THE ASHES`); }
      return true;
    }
    if (p.hp <= 0) {
      p.hp = 0;
      p.phase = Phase.Down;
      p.phaseT = secs(30) + m.bleedAdd; // Stopwatch
      dropBuffs(p);
      this.arsenal.dropCooked(p); // a live grenade in your hand falls at your feet
      p.chainT = 0; p.chainN = 0;
      this.emit(Ev.Down, [p.id]);
      // the downed cowboy calls for help on everyone's visor (MarkKind.NeedHand)
      this.emit(Ev.Mark, [p.id, 3, p.x, p.y, p.z, 0]);
      this.nemesis.onDown(p.id, src ? src.type : -1, this);
    }
    return true;
  }

  /** A cowboy hurt by a cowboy (friendly fire is always on): tell everyone who, for the callouts. */
  friendlyHurt(p: PlayerState, by: number, dmg: number, fx: number, fz: number, kx = 0, kz = 0, bypassShield = false) {
    const landed = this.hurtPlayer(p, dmg, fx, fz, 0, kx, kz, bypassShield);
    if (landed && by >= 0) this.emit(Ev.FriendlyHit, [p.id, by, Math.round(dmg), p.phase === Phase.Down ? 1 : 0]);
    return landed;
  }

  private downedTick(p: PlayerState) {
    // nobody left who could revive you: don't make you watch a 30 s timer
    const helpers = [...this.players.values()].some((o) => o !== p && o.phase === Phase.Alive && !o.paused);
    if (!helpers && p.phaseT > secs(3)) p.phaseT = secs(3);
    if (p.phaseT <= 0) {
      if (this.lives > 0) {
        this.lives--;
        this.emit(Ev.Lives, [this.lives]);
        respawnAt(p, this.world, { x: p.lastGx, y: p.lastGy + 0.1, z: p.lastGz });
        this.emit(Ev.Respawn, [p.id]);
      } else {
        p.phase = Phase.Dead;
        if ([...this.players.values()].every((o) => o.phase !== Phase.Alive)) wipeRun(this);
      }
    }
  }

  private stepRevives(active: PlayerState[]) {
    for (const d of active) {
      if (d.phase !== Phase.Down || d.paused) continue;
      let reviving = false;
      for (const r of active) {
        if (r === d || r.phase !== Phase.Alive || r.paused || r.skyT > 0) continue;
        // the reviver's last APPLIED buttons, not this tick's input: a late input (applied when it lands) must
        // not read as letting go of □
        if (!(r.prevButtons & Btn.Interact)) continue;
        if (len2(r.x - d.x, r.z - d.z) < 2.6 && Math.abs(r.y - d.y) < 2) {
          reviving = true;
          if (++d.reviveT >= Math.round(secs(2) * modsOf(r).reviveMul)) {
            d.phase = Phase.Alive; d.hp = 45; d.reviveT = 0; d.iframes = secs(2);
            r.iframes = Math.max(r.iframes, secs(1)); // the reviver gets a beat to turn around
            d.shield = CLASSES[d.cls].shieldMax * 0.5;
            this.emit(Ev.Revive, [d.id, r.id]);
          }
          break;
        }
      }
      if (!reviving && d.reviveT > 0) d.reviveT = Math.max(0, d.reviveT - 2);
    }
  }

  // ------------------------------------------------------------------ bullets
  fireBullet(kind: BKind, ox: number, oy: number, oz: number, vx: number, vy: number, vz: number, owner: number) {
    const id = this.nextBulletId++ & 0xffff || (this.nextBulletId++ & 0xffff);
    const b = makeBullet(id, kind, this.tick, ox, oy, oz, vx, vy, vz, owner);
    const shooter = this.enemies.find((e) => e.id === owner);
    b.src = shooter ? { type: shooter.type, nemesis: shooter.nemesis } : null;
    this.bullets.set(id, b);
    this.emit(Ev.BulletSpawn, [id, kind, this.tick, ox, oy, oz, vx, vy, vz, owner]);
  }

  private stepBullets() {
    for (const b of this.bullets.values()) {
      const res = advanceBullet(b, this.tick, this.world);
      const def = BULLETS[b.kind];
      let hitPid = 255;
      // Aegis Canopy: enemy fire dies at the dome
      if (this.arsenal.zones.length && this.arsenal.blocksBullet(b.x, b.y, b.z)) {
        this.emit(Ev.BulletEnd, [b.id, b.x, b.y, b.z, 255]);
        this.bullets.delete(b.id);
        continue;
      }
      // Magnetic Skin: the field around a buffed cowboy swats most robot fire out of the air
      if (this.magnetCatches(b)) {
        this.emit(Ev.BulletEnd, [b.id, b.x, b.y, b.z, 255]);
        this.bullets.delete(b.id);
        continue;
      }
      if (def.explode === 0) {
        for (const p of this.players.values()) {
          if (!isTargetable(p) || p.phase !== Phase.Alive) continue;
          if (bulletHitsCapsule(b, p.x, p.y, p.z, PLAYER_RADIUS)) {
            if (p.cls === ClassId.Phantom && p.reflectT > 0) { // the warp's reflection window (WARP_REFLECT)
              // warp reflection: the shot primes its shooter instead
              const shooter = this.enemies.find((e) => e.id === b.owner);
              if (shooter) this.prime(shooter, p);
              hitPid = p.id;
              break;
            }
            if (p.iframes > 0) continue; // rolled through it
            if (this.hurtPlayer(p, def.damage, b.ox, b.oz, 0, 0, 0, false, b.src)) { const sh = this.enemies.find((e) => e.id === b.owner); if (sh && sh.armorEl !== Elem.None) procPlayer(this, p, sh.armorEl, 1); }
            hitPid = p.id;
            break;
          }
        }
      }
      // a robot's bolt that hit a wall chips it (Godot: enemy bolts damage buildings too)
      if (res === 1 && hitPid === 255 && def.explode === 0) shotHitsWorld(this, b.x, b.y, b.z, def.damage);
      if (hitPid !== 255 || res !== 0) {
        if (def.explode > 0 && res !== 2) {
          if (b.kind === BKind.GasCan) this.openGas(b.x, b.y, b.z, ENGINEER.gasR, ENGINEER.gasDps, ENGINEER.gasSecs);
          else if (b.kind === BKind.IceNade || b.kind === BKind.MagNade) {
            this.explode(b.x, b.y, b.z, def.explode, def.damage, 4, b.kind === BKind.IceNade ? Proc.Ice : Proc.Magnetic, b.src);
          } else this.explode(b.x, b.y, b.z, def.explode, def.damage, b.kind === BKind.Boulder ? 3 : 2, Proc.None, b.src);
        }
        this.emit(Ev.BulletEnd, [b.id, b.x, b.y, b.z, hitPid]);
        this.bullets.delete(b.id);
      }
    }
  }

  /** Elemental Power: your hits carry the element (about one in three procs it). A gun's installed element (sim/elements.ts)
   *  rolls by its level on every hit; a hit on a hazard it beats reacts with it. */
  private elemHit(p: PlayerState, e: Enemy, dmg = 10, hx = e.x, hy = e.y + 1, hz = e.z) {
    if (e.dead) return;
    if (p.puAct === PU.ElementalPower && this.rng.next() < ELEM_PROC_CHANCE) this.arsenal.proc(e, puElem(p), p.id);
    const gEl = gunElem(p, p.wSlot);
    if (gEl === Elem.None) return;
    tryCombo(this, this.hazards, gEl, p.id, hx, hy, hz);
    const L = gunLevel(p, p.wSlot);
    if (tryProc(this, e, gEl, L, p.id, dmg) && L >= 3 && dmg >= HAZ_GUN_DMG && this.tick - (this.hazAt.get(p.id) ?? -9999) >= HAZ_GUN_CD) {
      // an L3+ gun's big hit leaves a small hazard where it landed (Godot: heavy ordnance seeds the field)
      const k = hazardKind(gEl);
      if (k !== -1) { this.hazAt.set(p.id, this.tick); placeHazard(this, this.hazards, k, p.id, e.x, this.world.support(e.x, e.z, e.y + 2), e.z, 0.5); }
    }
  }
  handOf(owner: number) { const p = this.players.get(owner); return p ? modsOf(p) : undefined; }
  /** the round mods (sim/cards.ts): Explosive bursts, Ricochet bounces to the nearest, Piercing carries on into the robot behind */
  private roundMods(p: PlayerState, m: Mods, e: Enemy, dmg: number, ap: number, hx: number, hy: number, hz: number, dx: number, dy: number, dz: number) {
    if (m.explosive) this.arsenal.blastAt(hx, hy, hz, 2, dmg * 0.2, ap, p.id);
    if (m.bounce) {
      let best: Enemy | null = null, bd = 8;
      for (const o of this.enemies) { if (o === e || o.dead) continue; const d = len2(o.x - hx, o.z - hz); if (d < bd) { bd = d; best = o; } }
      if (best) { this.damageEnemy(best, dmg * 0.5, ap, p.id, 0, best.x, best.y + ENEMIES[best.type].bodyY, best.z); this.emit(Ev.Ricochet, [p.id, hx, hy, hz, (best.x - hx) / (bd || 1), 0, (best.z - hz) / (bd || 1), bd, 2, best.id, activeWeaponId(p)]); }
    }
    if (m.pierce) {
      let best: Enemy | null = null, bt = 30;
      for (const o of this.enemies) {
        if (o === e || o.dead) continue;
        const d = ENEMIES[o.type], t = raySphere(hx, hy, hz, dx, dy, dz, o.x, o.y + d.bodyY, o.z, d.radius);
        if (t > 0.2 && t < bt) { bt = t; best = o; }
      }
      if (best) this.shotHitsEnemy(p, best, false, dx, dz, dmg, ap, hx + dx * bt, hy + dy * bt, hz + dz * bt, 1);
    }
  }
  /** C2S.Mark: a ping on a robot marks it for the Lens */
  mark(pid: number, eid: number) {
    const p = this.players.get(pid);
    if (!p || !eid || modsOf(p).lensMul === 1) return;
    const e = this.enemies.find((o) => o.id === eid && !o.dead);
    if (e) { e.lensBy = pid; e.lensT = secs(8); }
  }
  /** an elemental blast (a grenade, a rocket) landed: a hazard it beats reacts, else it leaves its own */
  elemBlast(elem: number, owner: number, x: number, y: number, z: number, scale = 1) {
    if (tryCombo(this, this.hazards, elem, owner, x, y, z)) return;
    const k = hazardKind(elem);
    if (k !== -1) placeHazard(this, this.hazards, k, owner, x, this.world.support(x, z, y + 2), z, scale);
  }
  /** an elemental robot's hit landed on a cowboy (enemies.ts AiHost) */
  elemHitPlayer(p: PlayerState, elem: number, level = 1) { procPlayer(this, p, elem, level); }
  /** an ordnance blast landed (arsenal): heavies it touches shed a plate (SHED_BLAST_DMG+), barrels it touches go off */
  onBlast(x: number, y: number, z: number, r: number, dmg: number, by: number) {
    if (dmg >= SHED_BLAST_DMG) for (const e of this.enemies) if (!e.dead && len2(e.x - x, e.z - z) < r + ENEMIES[e.type].radius && Math.abs(e.y - y) < 4) this.shedPanel(e);
    for (const b of this.barrels) if (!b.dead && b.fuse === 0 && len2(b.x - x, b.z - z) < r && Math.abs(b.y - y) < 4) { b.fuse = BARREL_FUSE_TICKS; b.by = by; }
    void dmg;
  }
  /** a robot births another (the Swarm Captain's brood): same wave, no affix */
  spawnAdhoc(type: EType, x: number, z: number) { return this.spawnEnemy(type, x, z); }

  private magnetCatches(b: Bullet) {
    for (const p of this.players.values()) {
      if (p.puAct !== PU.MagneticSkin || p.phase !== Phase.Alive) continue;
      const d = Math.hypot(b.x - p.x, b.y - (p.y + 1), b.z - p.z);
      if (d > MAGNET_R) continue;
      // decided once per bolt (by its id), so the same bolt never flickers between fates
      return (hash32(b.id, p.id, 0x3a9) % 1000) < MAGNET_CATCH * 1000;
    }
    return false;
  }

  explode(x: number, y: number, z: number, r: number, dmg: number, kind: number, proc: Proc = Proc.None, src: HitSrc | null = null) {
    this.emit(Ev.Explosion, [x, y, z, r, kind]);
    blastWorld(this, x, y, z, r, dmg);
    if (this.critters.length) {
      critterNoise(this.critters, x, z, 1.4);
      for (const c of this.critters) {
        if (c.st === CState.Dead || Math.hypot(c.x - x, c.y + 0.8 - y, c.z - z) > r) continue;
        if (hurtCritter(this.critters, c, dmg, -1, x, z)) this.emit(Ev.Critter, [c.id, 1, 255]);
      }
    }
    for (const p of this.players.values()) {
      const d = Math.sqrt((p.x - x) ** 2 + (p.y + 0.9 - y) ** 2 + (p.z - z) ** 2);
      if (d > r) continue;
      const f = 1 - (d / r) * 0.6;
      const kl = len2(p.x - x, p.z - z) || 1;
      // a magnetic proc skips the shield layer (elemental_system.gd _apply_magnetic)
      const landed = this.hurtPlayer(p, dmg * f, x, z, 0, ((p.x - x) / kl) * 8 * f, ((p.z - z) / kl) * 8 * f, proc === Proc.Magnetic, src);
      if (!landed) continue;
      if (proc === Proc.Ice && !immuneTo(p, Elem.Ice)) {
        // CLAUDE.md gotcha 11: 10 % slow per stack, capped at 3; stacks bleed off every 3 s
        p.iceN = Math.min(ICE_SLOW_MAX_STACKS, p.iceN + 1);
        p.iceT = secs(ICE_STACK_DECAY_SECS);
      } else if (proc === Proc.Magnetic) {
        p.shieldDelay = Math.max(p.shieldDelay, secs(MAGNETIC_SHIELD_BLOCK_SECS));
      }
    }
  }

  // ------------------------------------------------------------------ gas clouds
  openGas(x: number, y: number, z: number, r: number, dps: number, seconds: number, owner = -1) {
    let g = this.gas[0];
    for (const c of this.gas) { if (!c.on) { g = c; break; } if (c.t < g.t) g = c; } // full: replace the oldest
    const gy = this.world.support(x, z, y + 1);
    g.on = true; g.id = this.nextHazardId++ & 0xffff || this.nextHazardId++;
    g.x = x; g.y = gy > -1e6 ? gy : y; g.z = z; g.r = r; g.t = secs(seconds); g.dps = dps; g.owner = owner;
    this.emit(Ev.Hazard, [g.id, HAZ_GAS_EL, g.x, g.y, g.z, r, g.t]);
  }

  private stepGas() {
    for (const g of this.gas) {
      if (!g.on) continue;
      if (--g.t <= 0) { g.on = false; continue; }
      const pulse = g.t % GAS_PULSE === 0;
      const dmg = g.dps * GAS_PULSE / TICK_RATE;
      for (const p of this.players.values()) {
        // the posse's gas catches teammates at the softened hazard rate, never its thrower (gotcha 11)
        if (p.id === g.owner) continue;
        if (!isTargetable(p) || p.phase !== Phase.Alive) continue;
        if (len2(p.x - g.x, p.z - g.z) > g.r || Math.abs(p.y - g.y) > 2.5) continue;
        if (immuneTo(p, Elem.Acid)) continue; // Acid Power: the gas can't touch you
        p.gasT = Math.max(p.gasT, 2); // the slow holds while you stand in it
        if (pulse) { if (g.owner >= 0) this.friendlyHurt(p, g.owner, dmg * HAZARD_POSSE_MUL, g.x, g.z, 0, 0, true); else this.hurtPlayer(p, dmg, g.x, g.z, 0, 0, 0, true); }
      }
      if (!pulse) continue;
      for (const e of this.enemies) {
        if (e.dead || len2(e.x - g.x, e.z - g.z) > g.r + ENEMIES[e.type].radius * 0.5 || Math.abs(e.y - g.y) > 2.5) continue;
        this.damageEnemy(e, dmg, 0, g.owner, 0, e.x, e.y + 1, e.z, true, true);
        if (g.owner >= 0 && !e.dead) e.slowT = Math.max(e.slowT, 20);
      }
    }
  }

  spawnEnemy(type: EType, x: number, z: number) {
    const y = this.world.support(x, z, 60);
    const tierMul = Math.pow(1.18, runTier(this.run));
    const e = makeEnemy((this.nextEnemyId++ & 0xffff) || (this.nextEnemyId++ & 0xffff), type, x, y, z, tierMul, this.rng);
    e.armorEl = rollArmorEl(type, this.run.village, this.rng); // some robots carry an element (sim/elements.ts)
    for (let i = 0; i < e.hx.length; i++) { e.hx[i] = x; e.hy[i] = y; e.hz[i] = z; e.hyaw[i] = e.yaw; }
    this.enemies.push(e);
    return e;
  }

  /** The whole posse had left: a newcomer starts fresh, back in the saloon car (boarding, not the hotdog scene). */
  freshRun() {
    this.run = newRun();
    this.peakPosse = this.players.size;
    this.lives = this.posseLives();
    this.peakPosse = this.players.size;
    this.nemesis.reset();
    this.saloon.returnToSaloon(SaloonReason.Board);
  }

  get timeSec() { return this.tick / TICK_RATE; }
  get dtSec() { return DT; }
}

export { EState, HULL_MAX };
